import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import etcLogoWhite from "./etc-logo-white.png";
import arcadeIntroBackdrop from "./assets/arcade-intro-backdrop.png";
import arcadeMapBackdrop from "./assets/arcade-map-backdrop-v2.png";
import towerDropBackdrop from "./assets/tower-drop-arcade-backdrop.png";
import playerUfoSprite from "./assets/player-ufo.png";
import arcadePrizeCapsules from "./assets/arcade-prize-capsules.png";

const GAME_W = 980;
const GAME_H = 620;
const PLAYER_W = 48;
const PLAYER_H = 32;
const SPEED = 480;
const MAX_FRAME_DELTA = 34;
const INITIAL_PLAYER = { x: 478, y: 310 };
const INITIAL_TASKS = { wires: false, maze: false, shoot: false, flappy: false };

const funnyNames = [
  "Neon Striker",
  "Laser Captain",
  "Coin Runner",
  "Turbo Nova",
  "Pixel Pilot",
  "Chrome Blaster",
  "Vector Hero",
  "Arcade Ace",
];

const rooms = [
  {
    id: "bridge",
    name: "Bridge",
    x: 350,
    y: 220,
    w: 280,
    h: 190,
    color1: "#ff3b8d",
    color2: "#79155a",
    accent: "#ffeb3b",
    theme: "bridge",
  },
  {
    id: "spark",
    name: "Spark Bay",
    x: 40,
    y: 220,
    w: 250,
    h: 190,
    color1: "#00d9ff",
    color2: "#075985",
    accent: "#7cffd4",
    theme: "engine",
  },
  {
    id: "drift",
    name: "Vector Lab",
    x: 690,
    y: 220,
    w: 250,
    h: 190,
    color1: "#42ff87",
    color2: "#0f6b3d",
    accent: "#faff00",
    theme: "lab",
  },
  {
    id: "vault",
    name: "Boss Deck",
    x: 350,
    y: 30,
    w: 280,
    h: 145,
    color1: "#8b5cf6",
    color2: "#3b0764",
    accent: "#ff4fd8",
    theme: "vault",
  },
  {
    id: "cargo",
    name: "Turbo Dock",
    x: 350,
    y: 455,
    w: 280,
    h: 125,
    color1: "#ff8a00",
    color2: "#9a3412",
    accent: "#5ee7ff",
    theme: "cargo",
  },
];

const walls = [
  { x: 0, y: 0, w: GAME_W, h: 12 },
  { x: 0, y: GAME_H - 12, w: GAME_W, h: 12 },
  { x: 0, y: 0, w: 12, h: GAME_H },
  { x: GAME_W - 12, y: 0, w: 12, h: GAME_H },

  { x: 290, y: 220, w: 12, h: 26 },
  { x: 302, y: 246, w: 20, h: 16 },
  { x: 290, y: 372, w: 12, h: 38 },
  { x: 302, y: 346, w: 20, h: 16 },
  { x: 678, y: 220, w: 12, h: 26 },
  { x: 658, y: 246, w: 20, h: 16 },
  { x: 678, y: 372, w: 12, h: 38 },
  { x: 658, y: 346, w: 20, h: 16 },

  { x: 350, y: 175, w: 54, h: 12 },
  { x: 404, y: 187, w: 18, h: 18 },
  { x: 576, y: 175, w: 54, h: 12 },
  { x: 558, y: 187, w: 18, h: 18 },
  { x: 350, y: 443, w: 54, h: 12 },
  { x: 404, y: 425, w: 18, h: 18 },
  { x: 576, y: 443, w: 54, h: 12 },
  { x: 558, y: 425, w: 18, h: 18 },
];

const WALKABLE_ZONES = [
  { x: 40, y: 220, w: 250, h: 190 },
  { x: 350, y: 220, w: 280, h: 190 },
  { x: 690, y: 220, w: 250, h: 190 },
  { x: 350, y: 30, w: 280, h: 145 },
  { x: 350, y: 455, w: 280, h: 125 },
  { x: 290, y: 220, w: 60, h: 190 },
  { x: 630, y: 220, w: 60, h: 190 },
  { x: 350, y: 175, w: 280, h: 45 },
  { x: 350, y: 410, w: 280, h: 45 },
  { x: 224, y: 118, w: 46, h: 132 },
  { x: 224, y: 118, w: 126, h: 34 },
  { x: 236, y: 410, w: 46, h: 152 },
  { x: 236, y: 516, w: 114, h: 34 },
  { x: 630, y: 118, w: 210, h: 34 },
  { x: 794, y: 118, w: 46, h: 132 },
  { x: 630, y: 514, w: 210, h: 34 },
  { x: 794, y: 410, w: 46, h: 152 },
];

const taskPoints = [
  { id: "wires", name: "Power Links", room: "spark", x: 228, y: 366, color: "#faff00" },
  { id: "maze", name: "Tower Drop", room: "drift", x: 852, y: 360, color: "#00d9ff" },
  { id: "shoot", name: "Blast Raid", room: "vault", x: 566, y: 122, color: "#ff3b8d" },
  { id: "flappy", name: "Turbo Glide", room: "cargo", x: 576, y: 532, color: "#ff8a00" },
];
const STAGE_META = {
  wires: { label: "Power Links", color: "#faff00", short: "P" },
  maze: { label: "Tower Drop", color: "#00d9ff", short: "T" },
  shoot: { label: "Blast Raid", color: "#ff3b8d", short: "B" },
  flappy: { label: "Turbo Glide", color: "#ff8a00", short: "G" },
};

const PRIZE_CAPSULES = [
  { id: 1, position: "left center", glow: "#ff2d55", accent: "#faff00" },
  { id: 2, position: "center center", glow: "#00d9ff", accent: "#67e8f9" },
  { id: 3, position: "right center", glow: "#ff4fd8", accent: "#c4b5fd" },
];
const REWARD_CLAIM_URL = "https://www.etc.at/etc-arcade/";
const GUARANTEED_REWARD = {
  label: "Instant Rewards Unlocked!",
  text: "You scored instant rewards plus one entry for the grand prize draw.",
};

const WIRE_COLORS = ["#ff2d55", "#42ff87", "#00d9ff", "#faff00"];

const arcadeSound = (() => {
  let ctx = null;
  let master = null;
  let engineOsc = null;
  let engineGain = null;
  let glideOsc = null;
  let glideGain = null;
  let moveTickAt = 0;

  const getContext = () => {
    if (typeof window === "undefined") return null;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;

    if (!ctx) {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = 0.18;
      master.connect(ctx.destination);
    }

    if (ctx.state === "suspended") {
      ctx.resume();
    }

    return ctx;
  };

  const tone = (frequency, duration = 0.12, type = "square", gain = 0.22, when = 0, endFrequency = frequency) => {
    const audio = getContext();
    if (!audio || !master) return;

    const start = audio.currentTime + when;
    const osc = audio.createOscillator();
    const amp = audio.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, start);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), start + duration);
    amp.gain.setValueAtTime(0.0001, start);
    amp.gain.exponentialRampToValueAtTime(gain, start + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(amp);
    amp.connect(master);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  };

  const noise = (duration = 0.18, gain = 0.2, filterFrequency = 1200, when = 0) => {
    const audio = getContext();
    if (!audio || !master) return;

    const start = audio.currentTime + when;
    const buffer = audio.createBuffer(1, Math.max(1, Math.floor(audio.sampleRate * duration)), audio.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = audio.createBufferSource();
    const filter = audio.createBiquadFilter();
    const amp = audio.createGain();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(filterFrequency, start);
    filter.Q.value = 3.8;
    amp.gain.setValueAtTime(gain, start);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(amp);
    amp.connect(master);
    source.start(start);
  };

  const setLoop = (loopName, active, frequency, type = "sawtooth", gain = 0.03) => {
    const audio = getContext();
    if (!audio || !master) return;

    const oscKey = loopName === "engine" ? "engineOsc" : "glideOsc";
    let osc = loopName === "engine" ? engineOsc : glideOsc;
    let amp = loopName === "engine" ? engineGain : glideGain;

    if (active && !osc) {
      osc = audio.createOscillator();
      amp = audio.createGain();
      osc.type = type;
      osc.frequency.value = frequency;
      amp.gain.value = 0.0001;
      osc.connect(amp);
      amp.connect(master);
      osc.start();
      if (oscKey === "engineOsc") {
        engineOsc = osc;
        engineGain = amp;
      } else {
        glideOsc = osc;
        glideGain = amp;
      }
    }

    if (!amp) return;
    const now = audio.currentTime;
    if (active) {
      osc.frequency.setTargetAtTime(frequency, now, 0.04);
      amp.gain.setTargetAtTime(gain, now, 0.035);
    } else {
      amp.gain.setTargetAtTime(0.0001, now, 0.05);
    }
  };

  return {
    unlock() {
      getContext();
    },
    button() {
      tone(620, 0.045, "square", 0.07, 0, 760);
    },
    startGame() {
      getContext();
      tone(220, 0.09, "square", 0.16);
      tone(440, 0.09, "square", 0.16, 0.09);
      tone(880, 0.16, "square", 0.18, 0.18);
    },
    introFlyby() {
      tone(180, 0.55, "sawtooth", 0.1, 0, 720);
      noise(0.44, 0.08, 700, 0.06);
    },
    move(active) {
      setLoop("engine", active, active ? 86 + Math.sin(Date.now() / 130) * 12 : 86, "sawtooth", 0.022);
      const now = performance.now();
      if (active && now - moveTickAt > 145) {
        moveTickAt = now;
        tone(145, 0.035, "square", 0.028, 0, 95);
      }
    },
    nearTask() {
      tone(740, 0.08, "triangle", 0.08);
      tone(1180, 0.08, "triangle", 0.06, 0.055);
    },
    roomChange() {
      tone(260, 0.06, "square", 0.05);
    },
    taskOpen() {
      tone(330, 0.08, "square", 0.11);
      tone(660, 0.08, "square", 0.11, 0.07);
    },
    taskClose() {
      tone(420, 0.06, "square", 0.08, 0, 210);
    },
    wireSelect() {
      tone(920, 0.055, "triangle", 0.08);
    },
    wireMatch() {
      tone(660, 0.06, "square", 0.1);
      tone(1320, 0.08, "triangle", 0.08, 0.045);
    },
    wireMiss() {
      tone(220, 0.12, "sawtooth", 0.1, 0, 90);
      noise(0.08, 0.05, 550);
    },
    drop() {
      tone(980, 0.18, "square", 0.11, 0, 240);
    },
    land(perfect) {
      tone(perfect ? 880 : 520, 0.07, "square", 0.12);
      tone(perfect ? 1320 : 690, 0.08, "triangle", 0.08, 0.06);
    },
    crash() {
      tone(180, 0.32, "sawtooth", 0.16, 0, 48);
      noise(0.34, 0.2, 360, 0.02);
    },
    reset() {
      tone(280, 0.06, "square", 0.08);
      tone(420, 0.07, "square", 0.08, 0.055);
    },
    blast() {
      tone(1180, 0.05, "square", 0.13, 0, 520);
      noise(0.08, 0.08, 1800);
    },
    flap() {
      tone(540, 0.07, "triangle", 0.08, 0, 980);
    },
    gate() {
      tone(740, 0.05, "square", 0.08);
      tone(1110, 0.05, "square", 0.08, 0.04);
    },
    glide(active) {
      setLoop("glide", active, 170 + Math.sin(Date.now() / 180) * 24, "triangle", 0.018);
    },
    clear() {
      tone(520, 0.08, "square", 0.12);
      tone(780, 0.08, "square", 0.12, 0.08);
      tone(1040, 0.16, "square", 0.14, 0.16);
    },
    reward() {
      tone(392, 0.09, "triangle", 0.12);
      tone(784, 0.12, "triangle", 0.12, 0.09);
      tone(1568, 0.22, "triangle", 0.12, 0.2);
    },
  };
})();

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function rectHit(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function centerDistance(ax, ay, bx, by) {
  return Math.hypot(ax - bx, ay - by);
}

function pointInRect(px, py, rect) {
  return px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h;
}

function getPlayerCenter(player) {
  return {
    x: player.x + PLAYER_W / 2,
    y: player.y + PLAYER_H / 2,
  };
}

function getRoomNameForPlayer(player) {
  const center = getPlayerCenter(player);
  const room = rooms.find((r) => center.x >= r.x && center.x <= r.x + r.w && center.y >= r.y && center.y <= r.y + r.h);
  return room ? room.name : "Hallway";
}

function getNearTaskForPlayer(player, taskState) {
  const center = getPlayerCenter(player);
  return (
    taskPoints.find((task) => {
      if (taskState[task.id]) return false;
      return centerDistance(center.x, center.y, task.x, task.y) < 58;
    }) || null
  );
}

function canPlayerMoveTo(player) {
  const next = { x: player.x, y: player.y, w: PLAYER_W, h: PLAYER_H };
  const center = getPlayerCenter(player);
  const blocked = walls.some((wall) => rectHit(next, wall));
  const insideWalkableZone = WALKABLE_ZONES.some((zone) => pointInRect(center.x, center.y, zone));
  return !blocked && insideWalkableZone;
}

function pickReward() {
  return GUARANTEED_REWARD;
}

function GameButton({ children, onClick, disabled, style }) {
  return (
    <button
      className="arcade-button"
      onClick={(event) => {
        if (!disabled) {
          arcadeSound.button();
        }
        if (onClick) {
          onClick(event);
        }
      }}
      disabled={disabled}
      style={{
        background: disabled
          ? "linear-gradient(180deg, #3a4254, #202637)"
          : "linear-gradient(180deg, #faff00 0%, #ff8a00 58%, #ff2d55 100%)",
        color: disabled ? "#9ca3af" : "#130516",
        border: disabled ? "2px solid rgba(255,255,255,0.08)" : "2px solid rgba(255,255,255,0.72)",
        borderRadius: 10,
        padding: "12px 18px 11px",
        fontWeight: 900,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        cursor: disabled ? "default" : "pointer",
        boxShadow: disabled
          ? "inset 0 -4px 0 rgba(0,0,0,0.24)"
          : "0 0 18px rgba(255,45,85,0.42), 0 12px 0 #5b1034, 0 18px 24px rgba(0,0,0,0.36)",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function ScaledStage({ width, height, className, frameClassName = "", style, frameStyle, maxScale = 1, overlay, children, ...frameProps }) {
  const frameRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const updateScale = () => {
      const rect = frame.getBoundingClientRect();
      const nextScale = Math.min(rect.width / width, rect.height / height);
      setScale(Number.isFinite(nextScale) && nextScale > 0 ? Math.min(maxScale, nextScale) : 1);
    };

    updateScale();

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScale) : null;
    if (resizeObserver) {
      resizeObserver.observe(frame);
    }

    window.addEventListener("resize", updateScale);
    window.addEventListener("orientationchange", updateScale);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateScale);
      window.removeEventListener("orientationchange", updateScale);
    };
  }, [height, maxScale, width]);

  return (
    <div
      ref={frameRef}
      className={`scaled-stage-frame ${frameClassName}`.trim()}
      {...frameProps}
      style={{
        maxWidth: width,
        aspectRatio: `${width} / ${height}`,
        "--scaled-stage-scale": scale,
        ...frameStyle,
      }}
    >
      <div
        className={className}
        style={{
          ...style,
          position: "absolute",
          left: 0,
          top: 0,
          width,
          height,
          maxWidth: "none",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
      {overlay}
    </div>
  );
}

function MobileControls({ disabled, canAct, onMoveStart, onMoveEnd, onAction }) {
  const bindMove = (key) => ({
    onPointerDown: (event) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      onMoveStart(key);
    },
    onPointerUp: (event) => {
      event.preventDefault();
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      onMoveEnd(key);
    },
    onPointerCancel: () => onMoveEnd(key),
    onBlur: () => onMoveEnd(key),
    disabled,
  });

  return (
    <div className="mobile-game-controls" aria-hidden={disabled ? "true" : "false"}>
      <div className="mobile-dpad" aria-label="Mobile movement controls">
        <button type="button" className="mobile-dpad-button mobile-dpad-up" aria-label="Move up" {...bindMove("arrowup")}>
          {"\u2191"}
        </button>
        <button type="button" className="mobile-dpad-button mobile-dpad-left" aria-label="Move left" {...bindMove("arrowleft")}>
          {"\u2190"}
        </button>
        <button type="button" className="mobile-dpad-button mobile-dpad-down" aria-label="Move down" {...bindMove("arrowdown")}>
          {"\u2193"}
        </button>
        <button type="button" className="mobile-dpad-button mobile-dpad-right" aria-label="Move right" {...bindMove("arrowright")}>
          {"\u2192"}
        </button>
      </div>
      <button
        type="button"
        className="mobile-action-button"
        onPointerDown={(event) => {
          event.preventDefault();
          onAction();
        }}
        disabled={disabled || !canAct}
      >
        Play
      </button>
    </div>
  );
}

function OrientationNotice() {
  return (
    <div className="orientation-notice" role="status" aria-live="polite">
      <div className="orientation-notice-panel">
        <div className="orientation-notice-title">Please rotate your phone</div>
        <div className="orientation-notice-copy">The game fits better in landscape mode. On mobile, use the dedicated touch controls.</div>
      </div>
    </div>
  );
}

const PlayerAvatar = React.memo(
  React.forwardRef(function PlayerAvatar({ initialX, initialY }, ref) {
    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: PLAYER_W,
          height: PLAYER_H,
          transform: `translate3d(${initialX}px, ${initialY}px, 0) scaleX(1)`,
          transformOrigin: "center center",
          willChange: "transform",
          contain: "layout style",
          zIndex: 12,
          filter: "drop-shadow(0 10px 14px rgba(0,0,0,0.38)) drop-shadow(0 0 12px rgba(66,255,135,0.38))",
        }}
      >
        <div className="player-avatar">
          <div className="player-sprite-shadow" />
          <img className="player-ufo-sprite" src={playerUfoSprite} alt="" aria-hidden="true" />
        </div>
      </div>
    );
  })
);

function NeonPill({ x, y, w, color = "#faff00" }) {
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: 12,
        borderRadius: 999,
        background: `linear-gradient(180deg, rgba(255,255,255,0.6), ${color} 45%, rgba(15,23,42,0.8))`,
        border: "2px solid rgba(255,255,255,0.34)",
        boxShadow: `0 0 14px ${color}`,
      }}
    />
  );
}

function RoomInterior({ room }) {
  const accent = room.accent;
  const dark = room.color2;
  const tile = {
    position: "absolute",
    inset: 12,
    borderRadius: 14,
    background:
      `radial-gradient(circle at 30% 24%, ${accent}30 0 2px, transparent 3px), radial-gradient(circle at 74% 68%, rgba(255,255,255,0.3) 0 1px, transparent 2px), linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,0,0,0.26)), repeating-linear-gradient(90deg, transparent 0 23px, ${accent}18 23px 25px), linear-gradient(135deg, #07111f, ${dark})`,
    opacity: 0.94,
  };
  const rim = {
    position: "absolute",
    inset: 17,
    borderRadius: 12,
    border: `2px solid ${accent}88`,
    boxShadow: `inset 0 0 24px ${accent}30, inset 0 0 34px rgba(0,0,0,0.34), 0 0 16px ${accent}55`,
  };
  const orbit = (left, top, size, color = accent) => (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: size,
        height: size,
        borderRadius: "50%",
        border: `3px solid ${color}88`,
        borderLeftColor: "transparent",
        borderBottomColor: `${color}22`,
        boxShadow: `0 0 18px ${color}55, inset 0 0 18px ${color}20`,
        transform: "rotate(-18deg)",
      }}
    />
  );
  const energyCore = (left, top, size, color = accent, glow = "#ffffff") => (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle at 50% 45%, ${glow}, ${color} 38%, rgba(7,17,31,0.96) 70%)`,
        border: `3px solid ${color}`,
        boxShadow: `0 0 24px ${color}99, inset 0 0 16px rgba(255,255,255,0.28)`,
      }}
    />
  );
  const holoPanel = (left, top, width, height, color = accent) => (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        borderRadius: 6,
        background: `linear-gradient(180deg, ${color}44, rgba(7,17,31,0.82)), repeating-linear-gradient(0deg, transparent 0 7px, rgba(255,255,255,0.14) 7px 8px)`,
        border: `2px solid ${color}cc`,
        boxShadow: `0 0 18px ${color}77, inset 0 0 14px ${color}24`,
      }}
    />
  );
  const cornerPixels = (color = accent) => (
    <>
      {[0, 1, 2].map((i) => (
        <React.Fragment key={i}>
          <div style={{ position: "absolute", left: 24 + i * 12, top: 22, width: 7, height: 7, borderRadius: 2, background: color, boxShadow: `0 0 10px ${color}` }} />
          <div style={{ position: "absolute", right: 24 + i * 12, bottom: 22, width: 7, height: 7, borderRadius: 2, background: color, boxShadow: `0 0 10px ${color}` }} />
        </React.Fragment>
      ))}
    </>
  );
  const centerLane = (color = accent) => (
    <div
      style={{
        position: "absolute",
        left: "18%",
        right: "18%",
        top: "50%",
        height: 10,
        transform: "translateY(-50%)",
        borderRadius: 999,
        background: `linear-gradient(90deg, transparent, ${color}, rgba(255,255,255,0.82), ${color}, transparent)`,
        boxShadow: `0 0 20px ${color}88`,
        opacity: 0.78,
      }}
    />
  );
  const arcadeCabinet = (left, top, width, height, color, screen = "#101826") => (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        borderRadius: 8,
        background: `linear-gradient(180deg, rgba(255,255,255,0.16), rgba(0,0,0,0.34)), #111a2a`,
        border: `3px solid ${color}`,
        clipPath: "polygon(8% 0, 92% 0, 100% 16%, 92% 100%, 8% 100%, 0 16%)",
        boxShadow: `0 0 18px ${color}66, 0 8px 14px rgba(0,0,0,0.28)`,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: width * 0.16,
          right: width * 0.16,
          top: 10,
          height: 8,
          borderRadius: 2,
          background: "linear-gradient(90deg, #faff00, #ff8a00, #ff2d55)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: width * 0.18,
          right: width * 0.18,
          top: 24,
          height: Math.max(20, height * 0.32),
          borderRadius: 4,
          background: `radial-gradient(circle at 50% 45%, ${screen}, #07111f 68%)`,
          border: "2px solid rgba(255,255,255,0.2)",
          boxShadow: `inset 0 0 18px ${screen}99`,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: width * 0.22,
          right: width * 0.22,
          bottom: 16,
          height: 11,
          borderRadius: 999,
          background: "repeating-linear-gradient(90deg, #101826 0 8px, #faff00 8px 14px, #ff2d55 14px 20px)",
          border: "1px solid rgba(255,255,255,0.28)",
        }}
      />
    </div>
  );
  const wallGlow = (left, top, width, color = accent) => (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width,
        height: 8,
        borderRadius: 999,
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
        boxShadow: `0 0 14px ${color}`,
      }}
    />
  );

  if (room.theme === "bridge") {
    return (
      <>
        <div style={tile} />
        <div style={rim} />
        {cornerPixels("#ffeb3b")}
        {centerLane("#ffeb3b")}
        {orbit(88, 34, 104, "#00d9ff")}
        {energyCore(116, 62, 48, "#ff3b8d", "#ffeb3b")}
        {holoPanel(48, 88, 46, 34, "#00d9ff")}
        {holoPanel(186, 88, 46, 34, "#42ff87")}
        {wallGlow(54, 34, 172, "#00d9ff")}
      </>
    );
  }

  if (room.theme === "engine") {
    return (
      <>
        <div style={tile} />
        <div style={rim} />
        {cornerPixels("#7cffd4")}
        {centerLane("#7cffd4")}
        {orbit(60, 48, 74, "#faff00")}
        {energyCore(84, 72, 28, "#00d9ff", "#faff00")}
        {arcadeCabinet(154, 52, 56, 90, "#7cffd4", "#ff2d55")}
        {holoPanel(34, 104, 44, 30, "#00d9ff")}
        {wallGlow(78, 34, 96, "#faff00")}
      </>
    );
  }

  if (room.theme === "lab") {
    return (
      <>
        <div style={tile} />
        <div style={rim} />
        {cornerPixels("#42ff87")}
        {centerLane("#faff00")}
        {orbit(78, 36, 92, "#42ff87")}
        {energyCore(108, 66, 32, "#42ff87", "#eaff9d")}
        {wallGlow(68, 34, 112, "#42ff87")}
        {holoPanel(34, 86, 42, 42, "#00d9ff")}
        {holoPanel(190, 86, 42, 42, "#ff4fd8")}
      </>
    );
  }

  if (room.theme === "vault") {
    return (
      <>
        <div style={tile} />
        <div style={rim} />
        {cornerPixels("#ff4fd8")}
        {centerLane("#ff4fd8")}
        {orbit(86, 22, 108, "#8b5cf6")}
        {energyCore(112, 48, 56, "#ff4fd8", "#ffffff")}
        {wallGlow(66, 26, 148, "#8b5cf6")}
        <NeonPill x={86} y={112} w={108} color="#00d9ff" />
      </>
    );
  }

  return (
    <>
      <div style={tile} />
      <div style={rim} />
      {cornerPixels("#ff8a00")}
      {centerLane("#5ee7ff")}
      {orbit(94, 26, 84, "#ff8a00")}
      {energyCore(120, 52, 32, "#ff8a00", "#5ee7ff")}
      {holoPanel(42, 58, 42, 34, "#faff00")}
      {holoPanel(196, 58, 42, 34, "#5ee7ff")}
    </>
  );
}

function TaskMarker({ task, done }) {
  const color = done ? "#42ff87" : task.color;
  const glow = done ? "rgba(66,255,135,0.78)" : `${task.color}bb`;
  const symbol = done ? "OK" : STAGE_META[task.id]?.short || "!";

  return (
    <div
      className={done ? "task-marker task-marker-done" : "task-marker"}
      style={{
        position: "absolute",
        left: task.x - 34,
        top: task.y - 56,
        width: 96,
        height: 108,
        zIndex: 8,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 8,
          top: 72,
          width: 72,
          height: 22,
          borderRadius: 999,
          background: `radial-gradient(ellipse, ${color}44, rgba(15,23,42,0.08) 70%)`,
          filter: "blur(1px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 14,
          top: 42,
          width: 54,
          height: 54,
          borderRadius: 8,
          transform: "rotate(45deg)",
          background: `linear-gradient(135deg, rgba(255,255,255,0.2), rgba(7,17,31,0.92)), ${color}`,
          border: `3px solid ${color}`,
          boxShadow: `0 0 22px ${glow}, inset 0 0 16px rgba(255,255,255,0.14), 0 10px 18px rgba(0,0,0,0.28)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 25,
          top: 53,
          width: 32,
          height: 32,
          borderRadius: 5,
          background: `radial-gradient(circle at 50% 42%, rgba(255,255,255,0.9), ${color} 42%, #07111f 74%)`,
          border: "2px solid rgba(255,255,255,0.5)",
          boxShadow: `inset 0 0 12px ${color}, 0 0 14px ${glow}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 30,
          top: 58,
          width: 22,
          height: 20,
          color: done ? "#07111f" : "#ffffff",
          fontSize: done ? 9 : 14,
          fontWeight: 900,
          lineHeight: "20px",
          textAlign: "center",
          textShadow: done ? "none" : `0 0 10px ${color}`,
          transform: done ? "none" : "translateY(-1px)",
        }}
      >
        {symbol}
      </div>
      <div
        style={{
          position: "absolute",
          left: 19,
          top: 30,
          width: 44,
          height: 12,
          borderRadius: 999,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          boxShadow: `0 0 16px ${glow}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 22,
          top: 94,
          width: 38,
          height: 9,
          borderRadius: 999,
          background: `repeating-linear-gradient(90deg, ${color} 0 8px, rgba(255,255,255,0.24) 8px 12px, transparent 12px 18px)`,
          boxShadow: `0 0 12px ${glow}`,
        }}
      />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={done ? "task-marker-alert-done" : "task-marker-alert"}
          style={{
            position: "absolute",
            left: i < 2 ? 16 + i * 8 : 58 + (i - 2) * 8,
            top: i < 2 ? 36 : 86,
            width: 5,
            height: 5,
            borderRadius: 2,
            background: color,
            boxShadow: `0 0 10px ${glow}`,
          }}
        />
      ))}
      {!done && (
        <div
          className="task-marker-alert"
          style={{
            position: "absolute",
            left: 58,
            top: 18,
            width: 24,
            height: 24,
            borderRadius: 6,
            background: "linear-gradient(180deg, #faff00, #ff8a00 62%, #ff2d55)",
            border: "2px solid rgba(255,255,255,0.78)",
            color: "#170217",
            fontSize: 18,
            fontWeight: 900,
            lineHeight: "20px",
            textAlign: "center",
            boxShadow: "0 0 16px rgba(250,255,0,0.72), 0 8px 14px rgba(0,0,0,0.28)",
          }}
        >
          !
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 100,
          transform: "translateX(-50%)",
          padding: "4px 9px",
          borderRadius: 6,
          background: "rgba(7,17,31,0.92)",
          color,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.4,
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          border: `1px solid ${color}88`,
          boxShadow: `0 0 14px ${color}55, 0 6px 12px rgba(0,0,0,0.22)`,
        }}
      >
        {task.name}
      </div>
    </div>
  );
}

function WallBlock({ wall }) {
  return (
    <div
      className="wall-block"
      style={{
        position: "absolute",
        left: wall.x,
        top: wall.y,
        width: wall.w,
        height: wall.h,
        borderRadius: Math.min(12, wall.w / 2, wall.h / 2),
        background: "linear-gradient(180deg, #9aa6b8, #556072)",
        boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.08), 0 6px 12px rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 2,
          right: 2,
          top: 2,
          height: Math.min(8, wall.h / 2),
          borderRadius: 8,
          background: "rgba(255,255,255,0.18)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 1,
          top: 1,
          bottom: 1,
          width: Math.min(6, wall.w / 2),
          borderRadius: 8,
          background: "rgba(0,0,0,0.16)",
        }}
      />
    </div>
  );
}

function WiresTask({ onDone, onClose }) {
  const shuffled = useMemo(() => [...WIRE_COLORS].sort(() => Math.random() - 0.5), []);
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState({});

  useEffect(() => {
    if (Object.keys(matched).length === 4) {
      arcadeSound.clear();
      const t = setTimeout(onDone, 350);
      return () => clearTimeout(t);
    }
  }, [matched, onDone]);

  const leftDots = WIRE_COLORS.map((c, i) => ({ color: c, x: 90, y: 95 + i * 62 }));
  const rightDots = shuffled.map((c, i) => ({ color: c, x: 670, y: 95 + i * 62 }));
  const progress = Object.keys(matched).length;

  return (
    <Overlay title="Power Links" subtitle="Match the neon circuits from left to right before the grid burns out." onClose={onClose}>
      <ScaledStage
        width={760}
        height={390}
        className="mini-stage mini-stage-wires"
        frameClassName="task-stage-frame task-stage-frame-wide"
        style={{
          position: "relative",
          height: 390,
          borderRadius: 28,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            top: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 2,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: "rgba(15,23,42,0.7)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#e2e8f0",
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            Links: {progress}/4
          </div>
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: selected ? `${selected}33` : "rgba(15,23,42,0.7)",
              border: `1px solid ${selected ? selected : "rgba(255,255,255,0.1)"}`,
              color: selected ? selected : "#cbd5e1",
              boxShadow: selected ? `0 0 16px ${selected}55` : "none",
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            {selected ? "Active Wire" : "No Wire Active"}
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            left: 34,
            top: 62,
            bottom: 34,
            width: 112,
            borderRadius: 24,
            background: "linear-gradient(180deg, rgba(15,23,42,0.75), rgba(30,41,59,0.38))",
            border: "2px solid rgba(255,255,255,0.08)",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: 34,
            top: 62,
            bottom: 34,
            width: 112,
            borderRadius: 24,
            background: "linear-gradient(180deg, rgba(15,23,42,0.75), rgba(30,41,59,0.38))",
            border: "2px solid rgba(255,255,255,0.08)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 158,
            right: 158,
            top: 72,
            bottom: 46,
            borderRadius: 22,
            background:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 24px), radial-gradient(circle at center, rgba(59,130,246,0.12), transparent 60%)",
            opacity: 0.6,
          }}
        />

        <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
          {WIRE_COLORS.map((color, idx) => {
            if (!matched[color]) return null;
            const right = rightDots.find((d) => d.color === color);
            return (
              <line
                key={color}
                x1={leftDots[idx].x}
                y1={leftDots[idx].y}
                x2={right.x}
                y2={right.y}
                stroke={color}
                strokeWidth="10"
                strokeLinecap="round"
                style={{ filter: `drop-shadow(0 0 10px ${color})` }}
              />
            );
          })}
        </svg>

        {leftDots.map((dot) => (
          <button
            key={`l-${dot.color}`}
            onClick={() => {
              if (!matched[dot.color]) {
                arcadeSound.wireSelect();
                setSelected(dot.color);
              }
            }}
            style={{
              position: "absolute",
              left: dot.x - 24,
              top: dot.y - 24,
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: selected === dot.color ? "5px solid white" : "3px solid rgba(255,255,255,0.8)",
              background: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.7), transparent 35%), ${dot.color}`,
              boxShadow: selected === dot.color ? `0 0 24px ${dot.color}` : `0 0 14px ${dot.color}`,
              cursor: "pointer",
            }}
          />
        ))}

        {rightDots.map((dot) => (
          <button
            key={`r-${dot.color}`}
            onClick={() => {
              if (!selected || matched[dot.color]) return;
              if (selected === dot.color) {
                arcadeSound.wireMatch();
                setMatched((prev) => ({ ...prev, [dot.color]: true }));
              } else {
                arcadeSound.wireMiss();
              }
              setSelected(null);
            }}
            style={{
              position: "absolute",
              right: 66,
              top: dot.y - 24,
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.8)",
              background: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.7), transparent 35%), ${dot.color}`,
              boxShadow: `0 0 14px ${dot.color}`,
              cursor: "pointer",
            }}
          />
        ))}

        {leftDots.map((dot) => (
          <div
            key={`lp-${dot.color}`}
            style={{
              position: "absolute",
              left: 52,
              top: dot.y - 7,
              width: 22,
              height: 14,
              borderRadius: 999,
              background: "linear-gradient(180deg, #94a3b8, #475569)",
              border: "2px solid #1e293b",
            }}
          />
        ))}

        {rightDots.map((dot) => (
          <div
            key={`rp-${dot.color}`}
            style={{
              position: "absolute",
              right: 52,
              top: dot.y - 7,
              width: 22,
              height: 14,
              borderRadius: 999,
              background: "linear-gradient(180deg, #94a3b8, #475569)",
              border: "2px solid #1e293b",
            }}
          />
        ))}

        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 16,
            textAlign: "center",
            color: "#cbd5e1",
            fontWeight: 700,
            fontSize: 15,
          }}
        >
            {selected ? "Now hit the matching node on the right." : "Choose a neon node on the left."}
        </div>
      </ScaledStage>
    </Overlay>
  );
}

function MazeTask({ onDone, onClose }) {
  const STACK_WIDTH = 124;
  const TARGET_STACK = 5;
  const BOARD_WIDTH = 560;
  const BOARD_HEIGHT = 390;
  const FLOOR_Y = 26;
  const BLOCK_HEIGHT = 58;
  const HIT_TOLERANCE = 34;
  const DROP_DURATION = 360;
  const CRASH_DURATION = 980;
  const MOVE_SPEED = 500;

  const [stack, setStack] = useState([{ left: 218, width: STACK_WIDTH }]);
  const [dropState, setDropState] = useState("running");
  const [message, setMessage] = useState("Drop each neon block exactly on top of the last one.");
  const [towerTilt, setTowerTilt] = useState(0);
  const [droppingPiece, setDroppingPiece] = useState(null);
  const movingLeftRef = useRef(18);
  const directionRef = useRef(1);
  const movingBlockRef = useRef(null);
  const dropIdRef = useRef(0);
  const droppingPieceRef = useRef(null);
  const landedCountRef = useRef(1);
  const tiltTimerRef = useRef(null);
  const crashTimerRef = useRef(null);
  const activeDropId = droppingPiece?.id;

  useEffect(() => {
    if (dropState !== "running" || stack.length >= TARGET_STACK) return undefined;

    let raf = 0;
    let lastFrameTime = 0;

    const move = (time) => {
      if (!lastFrameTime) {
        lastFrameTime = time;
      }

      const frameDelta = Math.min(time - lastFrameTime, MAX_FRAME_DELTA);
      lastFrameTime = time;

      const currentWidth = stack[stack.length - 1].width;
      const maxLeft = BOARD_WIDTH - currentWidth - 18;
      let next = movingLeftRef.current + directionRef.current * MOVE_SPEED * (frameDelta / 1000);

      if (next <= 18) {
        next = 18;
        directionRef.current = 1;
      } else if (next >= maxLeft) {
        next = maxLeft;
        directionRef.current = -1;
      }

      movingLeftRef.current = next;
      if (movingBlockRef.current) {
        movingBlockRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
      }

      raf = requestAnimationFrame(move);
    };

    raf = requestAnimationFrame(move);
    return () => cancelAnimationFrame(raf);
  }, [dropState, stack]);

  useEffect(() => {
    if (landedCountRef.current >= TARGET_STACK) {
      setDropState("won");
      setMessage("Perfect stack. Vector Lab is online.");
      const timer = setTimeout(onDone, 350);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [stack.length, onDone]);

  useEffect(() => {
    return () => {
      if (tiltTimerRef.current) clearTimeout(tiltTimerRef.current);
      if (crashTimerRef.current) clearTimeout(crashTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!activeDropId) return undefined;
    const currentPiece = droppingPieceRef.current;

    const startTimer = setTimeout(() => {
      setDroppingPiece((prev) => (prev ? { ...prev, phase: "falling" } : prev));
    }, 0);

    const landTimer = setTimeout(() => {
      if (currentPiece.result === "crash") {
        arcadeSound.crash();
        setStack((prev) => [...prev, { left: currentPiece.left, width: STACK_WIDTH }]);
        setDroppingPiece(null);
        setDropState("crashing");
        setTowerTilt(currentPiece.offset < 0 ? -78 : 78);
        setMessage("Bad landing. The full tower is tipping over.");
        if (crashTimerRef.current) clearTimeout(crashTimerRef.current);
        crashTimerRef.current = setTimeout(() => {
          setDropState("missed");
          setMessage("The tower fell over. Press Try Again or Space to restart.");
        }, 980);
      } else {
        arcadeSound.land(Math.abs(currentPiece.offset) <= 8);
        const landingTilt = clamp(currentPiece.offset / 4.5, -8, 8);
        if (tiltTimerRef.current) clearTimeout(tiltTimerRef.current);
        setTowerTilt(landingTilt);
        tiltTimerRef.current = setTimeout(() => setTowerTilt(0), 170);
        setStack((prev) => [...prev, { left: currentPiece.left, width: STACK_WIDTH }]);
        landedCountRef.current = currentPiece.nextCount;
        setDropState("running");
        movingLeftRef.current = 44;
        directionRef.current = 1;
        if (movingBlockRef.current) {
          movingBlockRef.current.style.transform = "translate3d(44px, 0, 0)";
        }
        setDroppingPiece(null);
        setMessage(
          Math.abs(currentPiece.offset) <= 8
            ? `Perfect drop. ${currentPiece.nextCount}/${TARGET_STACK} tower blocks locked.`
            : `Close landing. The tower is wobbling. ${currentPiece.nextCount}/${TARGET_STACK} locked.`
        );
      }
    }, DROP_DURATION);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(landTimer);
    };
  }, [activeDropId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        if (dropState === "running") {
          dropBlock();
        } else if (dropState === "missed") {
          resetRun();
        }
      }
      if ((e.key === "r" || e.key === "R") && dropState === "missed") {
        resetRun();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const resetRun = () => {
    arcadeSound.reset();
    if (tiltTimerRef.current) clearTimeout(tiltTimerRef.current);
    if (crashTimerRef.current) clearTimeout(crashTimerRef.current);
    setStack([{ left: 218, width: STACK_WIDTH }]);
    movingLeftRef.current = 18;
    directionRef.current = 1;
    if (movingBlockRef.current) {
      movingBlockRef.current.style.transform = "translate3d(18px, 0, 0)";
    }
    setDropState("running");
    setTowerTilt(0);
    setDroppingPiece(null);
    landedCountRef.current = 1;
    setMessage("Drop each neon block exactly on top of the last one.");
  };

  const dropBlock = () => {
    const base = stack[stack.length - 1];
    const currentLeft = movingLeftRef.current;
    const offset = currentLeft - base.left;
    const centerDelta = Math.abs(offset);
    const targetBottom = FLOOR_Y + stack.length * (BLOCK_HEIGHT - 2);
    const id = dropIdRef.current + 1;
    dropIdRef.current = id;

    arcadeSound.drop();
    setDropState("dropping");
    setMessage(centerDelta > HIT_TOLERANCE ? "The block is dropping toward an unstable landing..." : "Block incoming. Line looks good.");
    const piece = {
      id,
      left: currentLeft,
      targetBottom,
      offset,
      nextCount: stack.length + 1,
      phase: "start",
      result: centerDelta > HIT_TOLERANCE ? "crash" : "landed",
    };
    droppingPieceRef.current = piece;
    setDroppingPiece(piece);
  };

  const placedCount = stack.length;
  const handleTowerTap = (event) => {
    event.preventDefault();
    if (dropState === "running") {
      dropBlock();
    } else if (dropState === "missed") {
      resetRun();
    }
  };

  const handleTowerModalTap = (event) => {
    if (event.pointerType === "mouse") return;
    if (event.target.closest("button")) return;
    handleTowerTap(event);
  };

  return (
    <Overlay
      title="Tower Drop"
      subtitle="Land 5 cabinet blocks in a row to stabilize the Vector Lab."
      onClose={onClose}
      modalProps={{
        className: "tower-drop-modal",
        onPointerDown: handleTowerModalTap,
      }}
      bodyProps={{
        className: "tower-drop-touch-body",
      }}
    >
      <div
        className="mini-stage mini-stage-stack mini-task-status"
        style={{
          marginTop: 6,
          marginBottom: 16,
          padding: "16px 18px",
          borderRadius: 12,
          background:
            "linear-gradient(180deg, rgba(12,4,28,0.96), rgba(5,8,22,0.9)), repeating-linear-gradient(90deg, rgba(250,255,0,0.12) 0 8px, rgba(255,45,85,0.1) 8px 16px, rgba(0,217,255,0.1) 16px 24px)",
          border: "3px solid rgba(250,255,0,0.42)",
          boxShadow: "0 0 24px rgba(255,45,85,0.24), inset 0 0 24px rgba(0,217,255,0.12)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ color: "#faff00", fontWeight: 900, textTransform: "uppercase", textShadow: "0 0 12px rgba(250,255,0,0.46)" }}>
            Blocks stacked: {placedCount}/{TARGET_STACK}
          </div>
          <div style={{ color: "#00d9ff", fontWeight: 900, textTransform: "uppercase", textShadow: "0 0 12px rgba(0,217,255,0.5)" }}>
            Goal: stack 5 space towers without missing
          </div>
        </div>
        <div style={{ marginTop: 10, color: "#ffe9ff", fontWeight: 800, textAlign: "center", textShadow: "0 0 10px rgba(255,79,216,0.36)" }}>{message}</div>
      </div>

      <ScaledStage
        width={BOARD_WIDTH}
        height={BOARD_HEIGHT}
        className="tower-drop-stage"
        frameClassName="task-stage-frame task-stage-frame-medium task-tap-stage-frame"
        role="button"
        aria-label={dropState === "missed" ? "Restart Tower Drop" : "Drop Tower Drop block"}
        tabIndex={0}
        onPointerDown={(event) => {
          event.stopPropagation();
          handleTowerTap(event);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleTowerTap(event);
          }
        }}
        style={{
          position: "relative",
          height: BOARD_HEIGHT,
          borderRadius: 28,
          overflow: "hidden",
          background:
            `linear-gradient(180deg, rgba(4,2,16,0.08), rgba(4,2,16,0.34)), url(${towerDropBackdrop}) center / cover no-repeat, linear-gradient(180deg, #090617, #100522 64%, #04020a)`,
          border: "4px solid rgba(250,255,0,0.72)",
          boxShadow:
            "inset 0 0 0 4px rgba(255,255,255,0.08), inset 0 0 42px rgba(0,217,255,0.22), 0 0 28px rgba(255,45,85,0.28), 0 22px 36px rgba(0,0,0,0.36)",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(180deg, rgba(255,255,255,0.1) 0 1px, transparent 1px 5px), radial-gradient(circle at 50% 42%, rgba(0,217,255,0.12), transparent 28%)",
            mixBlendMode: "screen",
            opacity: 0.32,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: "12px 14px",
            borderRadius: 20,
            border: "2px solid rgba(0,217,255,0.38)",
            boxShadow: "inset 0 0 24px rgba(0,217,255,0.18), 0 0 18px rgba(0,217,255,0.18)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 116,
            background:
              "linear-gradient(180deg, rgba(4,2,16,0) 0%, rgba(4,2,16,0.1) 22%, rgba(3,2,10,0.82) 100%)",
          }}
        />
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${(i * 53) % BOARD_WIDTH}px`,
              top: `${18 + ((i * 37) % 130)}px`,
              width: 2,
              height: 2,
              borderRadius: 999,
              background: i % 4 === 0 ? "#faff00" : i % 3 === 0 ? "#ff4fd8" : "#b8fff2",
              boxShadow: i % 4 === 0 ? "0 0 8px rgba(250,255,0,0.8)" : "0 0 8px rgba(0,217,255,0.74)",
              opacity: 0.9,
            }}
          />
        ))}

        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={`sky-${i}`}
            style={{
              position: "absolute",
              left: 18 + i * 62,
              bottom: 18,
              width: 26 + (i % 3) * 12,
              height: 46 + (i % 4) * 18,
              borderRadius: "5px 5px 0 0",
              background:
                i % 2 === 0
                  ? "linear-gradient(180deg, rgba(0,217,255,0.34), rgba(13,10,33,0.92))"
                  : "linear-gradient(180deg, rgba(255,45,85,0.32), rgba(13,10,33,0.92))",
              border: "1px solid rgba(250,255,0,0.18)",
              boxShadow: "0 0 16px rgba(0,217,255,0.12)",
            }}
          />
        ))}

        <div
          style={{
            position: "absolute",
            left: 78,
            right: 78,
            bottom: 26,
            height: 18,
            borderRadius: 999,
            background: "rgba(15,23,42,0.5)",
            filter: "blur(2px)",
          }}
        />

        <div
          style={{
            position: "absolute",
            left: 78,
            right: 78,
            bottom: 20,
            height: 18,
            borderRadius: 4,
            background:
              "linear-gradient(180deg, #faff00 0%, #ff8a00 42%, #ff2d55 100%), repeating-linear-gradient(90deg, rgba(8,3,18,0.42) 0 14px, transparent 14px 28px)",
            border: "2px solid rgba(255,255,255,0.42)",
            boxShadow: "0 0 20px rgba(250,255,0,0.28)",
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            transform:
              dropState === "crashing" || dropState === "missed"
                ? `translate(${towerTilt > 0 ? 18 : -18}px, 8px) rotate(${towerTilt * 0.3}deg)`
                : `rotate(${towerTilt}deg)`,
            transformOrigin: "center bottom",
            transition:
              dropState === "crashing"
                ? `transform ${CRASH_DURATION}ms cubic-bezier(0.18, 0.72, 0.24, 1)`
                : dropState === "missed"
                  ? "none"
                  : "transform 160ms ease-out",
            willChange: "transform",
          }}
        >
          {stack.map((block, index) => {
            const bottom = FLOOR_Y + index * (BLOCK_HEIGHT - 2);
            const crashDirection = towerTilt >= 0 ? 1 : -1;
            const spreadDirection = index % 2 === 0 ? 1 : -1;
            const crashOffsetX =
              dropState === "crashing" || dropState === "missed"
                ? crashDirection * (18 + index * 10) + spreadDirection * (26 + index * 8)
                : 0;
            const fallDistance = BOARD_HEIGHT - bottom - BLOCK_HEIGHT - 14;
            const crashOffsetY = dropState === "crashing" || dropState === "missed" ? fallDistance : 0;
            const crashRotate =
              dropState === "crashing" || dropState === "missed"
                ? crashDirection * (10 + index * 5) + spreadDirection * (12 + index * 4)
                : 0;
            return (
              <div
                key={`${block.left}-${index}`}
                style={{
                  position: "absolute",
                  left: block.left,
                  bottom,
                  width: block.width,
                  height: BLOCK_HEIGHT,
                  borderRadius: "14px 14px 6px 6px",
                  background:
                    index === 0
                      ? "linear-gradient(180deg, #b8fff2 0%, #00d9ff 44%, #1456ff 100%)"
                      : index % 2 === 0
                        ? "linear-gradient(180deg, #fffb8a 0%, #ff8a00 44%, #ff2d55 100%)"
                        : "linear-gradient(180deg, #ffd1ff 0%, #ff4fd8 44%, #6d28d9 100%)",
                  border: "3px solid rgba(255,255,255,0.84)",
                  boxShadow: "0 12px 18px rgba(0,0,0,0.28), 0 0 22px rgba(0,217,255,0.28), inset 0 0 18px rgba(255,255,255,0.14)",
                  transform: `translate(${crashOffsetX}px, ${crashOffsetY}px) rotate(${crashRotate}deg)`,
                  transition:
                    dropState === "crashing"
                      ? `transform ${CRASH_DURATION - 60 + index * 70}ms cubic-bezier(0.18, 0.74, 0.22, 1)`
                      : dropState === "missed"
                        ? "none"
                        : "transform 160ms ease-out",
                  willChange: "transform",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 8,
                    right: 8,
                    top: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.42)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 10,
                    right: 10,
                    bottom: 8,
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 6,
                  }}
                >
                  {Array.from({ length: 4 }).map((_, windowIndex) => (
                    <div
                      key={windowIndex}
                      style={{
                    height: 18,
                    borderRadius: 4,
                    background: windowIndex % 2 === 0 ? "rgba(250,255,0,0.9)" : "rgba(0,217,255,0.86)",
                    border: "1px solid rgba(255,255,255,0.36)",
                    boxShadow: windowIndex % 2 === 0 ? "0 0 8px rgba(250,255,0,0.62)" : "0 0 8px rgba(0,217,255,0.58)",
                  }}
                />
                  ))}
                </div>
                <div
                  style={{
                    position: "absolute",
                    left: block.width / 2 - 2,
                    top: -12,
                    width: 4,
                    height: 12,
                    borderRadius: 999,
                    background: "rgba(224,242,254,0.9)",
                  }}
                />
              </div>
            );
          })}
        </div>

        {droppingPiece && (
          <div
            style={{
              position: "absolute",
              left: droppingPiece.left,
              top: 40,
              width: STACK_WIDTH,
              height: BLOCK_HEIGHT,
              borderRadius: "12px 12px 6px 6px",
              background: "linear-gradient(180deg, #ffd1ff 0%, #ff4fd8 44%, #6d28d9 100%)",
              border: "3px solid rgba(255,255,255,0.84)",
              boxShadow: "0 0 24px rgba(255,79,216,0.44), inset 0 0 16px rgba(255,255,255,0.14)",
              transform:
                droppingPiece.phase === "falling"
                  ? `translateY(${BOARD_HEIGHT - droppingPiece.targetBottom - BLOCK_HEIGHT - 40}px)`
                  : "translateY(0px)",
              transition: `transform ${DROP_DURATION}ms linear`,
              willChange: "transform",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 8,
                right: 8,
                top: 6,
                height: 6,
                borderRadius: 999,
                background: "rgba(255,255,255,0.46)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 10,
                right: 10,
                bottom: 6,
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 6,
              }}
            >
              {Array.from({ length: 4 }).map((_, windowIndex) => (
                <div
                  key={windowIndex}
                  style={{
                    height: 12,
                    borderRadius: 4,
                    background: windowIndex % 2 === 0 ? "rgba(250,255,0,0.92)" : "rgba(0,217,255,0.88)",
                    border: "1px solid rgba(255,255,255,0.34)",
                    boxShadow: windowIndex % 2 === 0 ? "0 0 8px rgba(250,255,0,0.6)" : "0 0 8px rgba(0,217,255,0.56)",
                  }}
                />
              ))}
            </div>
            <div
              style={{
                position: "absolute",
                left: STACK_WIDTH / 2 - 2,
                top: -12,
                width: 4,
                height: 12,
                borderRadius: 999,
                background: "rgba(255,248,220,0.95)",
              }}
            />
          </div>
        )}

        {dropState === "running" && stack.length < TARGET_STACK && (
          <div
            ref={movingBlockRef}
            style={{
              position: "absolute",
              left: 0,
              top: 40,
              width: STACK_WIDTH,
              height: BLOCK_HEIGHT,
              borderRadius: "12px 12px 6px 6px",
              background: "linear-gradient(180deg, #fffb8a 0%, #ff8a00 44%, #ff2d55 100%)",
              border: "3px solid rgba(255,255,255,0.84)",
              boxShadow: "0 0 26px rgba(250,255,0,0.42), inset 0 0 16px rgba(255,255,255,0.16)",
              transform: `translate3d(${movingLeftRef.current}px, 0, 0)`,
              willChange: "transform",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 8,
                right: 8,
                top: 6,
                height: 6,
                borderRadius: 999,
                background: "rgba(255,255,255,0.46)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 10,
                right: 10,
                bottom: 6,
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 6,
              }}
            >
              {Array.from({ length: 4 }).map((_, windowIndex) => (
                <div
                  key={windowIndex}
                  style={{
                    height: 12,
                    borderRadius: 4,
                    background: windowIndex % 2 === 0 ? "rgba(250,255,0,0.92)" : "rgba(0,217,255,0.88)",
                    border: "1px solid rgba(255,255,255,0.34)",
                    boxShadow: windowIndex % 2 === 0 ? "0 0 8px rgba(250,255,0,0.6)" : "0 0 8px rgba(0,217,255,0.56)",
                  }}
                />
              ))}
            </div>
            <div
              style={{
                position: "absolute",
                left: STACK_WIDTH / 2 - 2,
                top: -12,
                width: 4,
                height: 12,
                borderRadius: 999,
                background: "rgba(255,248,220,0.95)",
              }}
            />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            left: 20,
            top: 18,
            padding: "8px 12px",
            borderRadius: 4,
            background: "linear-gradient(180deg, rgba(250,255,0,0.92), rgba(255,138,0,0.88))",
            border: "2px solid rgba(255,255,255,0.68)",
            color: "#170217",
            fontWeight: 900,
            fontSize: 13,
            textTransform: "uppercase",
            boxShadow: "0 0 18px rgba(250,255,0,0.36), 0 6px 0 rgba(109,18,56,0.72)",
          }}
        >
          Neon Drop Yard
        </div>
      </ScaledStage>

      <div className="task-action-row task-action-row-mobile-secondary" style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 20 }}>
        <GameButton onClick={dropState === "running" ? dropBlock : resetRun} disabled={dropState === "crashing" || dropState === "dropping"}>
          {dropState === "running" ? "Drop Block" : dropState === "dropping" ? "Block Falling..." : dropState === "crashing" ? "Stack Falling..." : "Try Again"}
        </GameButton>
        <GameButton
          onClick={() => {
            arcadeSound.taskClose();
            onClose();
          }}
          style={{ background: "linear-gradient(180deg, #475569, #334155)" }}
        >
          Leave
        </GameButton>
      </div>
    </Overlay>
  );
}

function ShootTask({ onDone, onClose }) {
  const [ufos, setUfos] = useState([
    {
      id: 1,
      x: 20,
      y: 35,
      alive: true,
      driftDuration: 2900,
      delay: -400,
      path: [
        [-48, 24],
        [34, -42],
        [-14, -26],
        [52, 30],
        [-38, -10],
        [44, -34],
      ],
    },
    {
      id: 2,
      x: 48,
      y: 60,
      alive: true,
      driftDuration: 3150,
      delay: -900,
      path: [
        [50, -34],
        [-54, 26],
        [22, 44],
        [-36, -46],
        [58, 12],
        [-46, 34],
      ],
    },
    {
      id: 3,
      x: 72,
      y: 28,
      alive: true,
      driftDuration: 2750,
      delay: -1300,
      path: [
        [-58, -26],
        [42, 38],
        [-28, 48],
        [18, -42],
        [-52, 18],
        [48, -30],
      ],
    },
    {
      id: 4,
      x: 30,
      y: 72,
      alive: true,
      driftDuration: 3350,
      delay: -600,
      path: [
        [42, 40],
        [-50, -22],
        [36, -50],
        [-18, 34],
        [56, -8],
        [-44, -38],
      ],
    },
    {
      id: 5,
      x: 66,
      y: 48,
      alive: true,
      driftDuration: 3050,
      delay: -1700,
      path: [
        [-52, 42],
        [46, -44],
        [-36, -18],
        [58, 26],
        [-18, 50],
        [40, -36],
      ],
    },
  ]);

  useEffect(() => {
    if (ufos.every((u) => !u.alive)) {
      arcadeSound.clear();
      const t = setTimeout(onDone, 300);
      return () => clearTimeout(t);
    }
  }, [ufos, onDone]);

  const blastUfo = (id) => {
    arcadeSound.blast();
    setUfos((prev) => prev.map((ufo) => (ufo.id === id ? { ...ufo, alive: false } : ufo)));
  };

  return (
    <Overlay title="Blast Raid" subtitle="Clear all 5 incoming targets before they overload the cabinet." onClose={onClose}>
      <div
        style={{
          marginTop: 4,
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 8,
          background: "linear-gradient(180deg, rgba(255,45,85,0.16), rgba(0,217,255,0.08))",
          border: "2px solid rgba(255,45,85,0.28)",
          color: "#faff00",
          fontWeight: 900,
          textAlign: "center",
          textTransform: "uppercase",
          textShadow: "0 0 10px rgba(250,255,0,0.36)",
        }}
      >
        Click the UFOs to hit them.
      </div>
      <ScaledStage
        width={720}
        height={380}
        className="blast-stage"
        frameClassName="task-stage-frame task-stage-frame-wide"
        style={{
          position: "relative",
          height: 380,
          borderRadius: 28,
          overflow: "hidden",
        }}
      >
        {ufos.map((ufo) =>
          ufo.alive ? (
            <button
              key={ufo.id}
              className="blast-target"
              aria-label={`Blast target ${ufo.id}`}
              onPointerDown={(event) => {
                event.preventDefault();
                blastUfo(ufo.id);
              }}
              style={{
                position: "absolute",
                left: `${ufo.x}%`,
                top: `${ufo.y}%`,
                width: 86,
                height: 86,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                touchAction: "none",
                "--drift-x-0": `${ufo.path[0][0]}px`,
                "--drift-y-0": `${ufo.path[0][1]}px`,
                "--drift-x-1": `${ufo.path[1][0]}px`,
                "--drift-y-1": `${ufo.path[1][1]}px`,
                "--drift-x-2": `${ufo.path[2][0]}px`,
                "--drift-y-2": `${ufo.path[2][1]}px`,
                "--drift-x-3": `${ufo.path[3][0]}px`,
                "--drift-y-3": `${ufo.path[3][1]}px`,
                "--drift-x-4": `${ufo.path[4][0]}px`,
                "--drift-y-4": `${ufo.path[4][1]}px`,
                "--drift-x-5": `${ufo.path[5][0]}px`,
                "--drift-y-5": `${ufo.path[5][1]}px`,
                "--drift-duration": `${ufo.driftDuration}ms`,
                animationDelay: `${ufo.delay}ms`,
              }}
            >
              🛸
            </button>
          ) : (
            <div
              key={ufo.id}
              className="blast-pop"
              style={{
                position: "absolute",
                left: `${ufo.x}%`,
                top: `${ufo.y}%`,
                width: 64,
                height: 64,
              }}
            >
              💥
            </div>
          )
        )}

        <div
          className="blast-counter"
          style={{
            position: "absolute",
            bottom: 14,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 16px",
            borderRadius: 14,
            fontWeight: 700,
          }}
        >
          Remaining: {ufos.filter((u) => u.alive).length}
        </div>
      </ScaledStage>
    </Overlay>
  );
}

function FlappyTask({ onDone, onClose }) {
  const GAME_WIDTH = 560;
  const GAME_HEIGHT = 320;
  const UFO_X = 110;
  const UFO_SIZE = 34;
  const GAP_SIZE = 126;
  const PIPE_WIDTH = 56;
  const TARGET_SCORE = 10;
  const GRAVITY = 760;
  const FLAP_VELOCITY = -275;
  const PIPE_SPEED = 145;

  const createPipes = useCallback(() => [
    { id: 1, x: 400, gapTop: 70, passed: false },
    { id: 2, x: 620, gapTop: 120, passed: false },
    { id: 3, x: 840, gapTop: 92, passed: false },
  ], []);

  const [glide, setGlide] = useState(() => ({
    ufoY: 160,
    velocity: 0,
    pipes: createPipes(),
    score: 0,
    failed: false,
    started: false,
  }));
  const glideRef = useRef(glide);
  const lastScoreRef = useRef(0);
  const failedSoundRef = useRef(false);

  const publishGlide = useCallback((nextGlide) => {
    glideRef.current = nextGlide;
    setGlide(nextGlide);
  }, []);

  const flap = useCallback(() => {
    const current = glideRef.current;
    if (current.failed || current.score >= TARGET_SCORE) return;
    arcadeSound.flap();
    publishGlide({ ...current, started: true, velocity: FLAP_VELOCITY });
  }, [FLAP_VELOCITY, TARGET_SCORE, publishGlide]);

  const resetGlide = useCallback(() => {
    arcadeSound.reset();
    failedSoundRef.current = false;
    lastScoreRef.current = 0;
    publishGlide({
      ufoY: 160,
      velocity: 0,
      pipes: createPipes(),
      score: 0,
      failed: false,
      started: false,
    });
  }, [createPipes, publishGlide]);

  useEffect(() => {
    if (glide.score >= TARGET_SCORE) {
      arcadeSound.clear();
      const timer = setTimeout(onDone, 350);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [glide.score, onDone]);

  useEffect(() => {
    if (glide.score > lastScoreRef.current) {
      arcadeSound.gate();
    }
    lastScoreRef.current = glide.score;
  }, [glide.score]);

  useEffect(() => {
    arcadeSound.glide(glide.started && !glide.failed && glide.score < TARGET_SCORE);
    if (glide.failed && !failedSoundRef.current) {
      failedSoundRef.current = true;
      arcadeSound.crash();
    }
    return () => arcadeSound.glide(false);
  }, [glide.failed, glide.score, glide.started]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        if (glideRef.current.failed) {
          resetGlide();
        } else {
          flap();
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        flap();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flap, resetGlide]);

  useEffect(() => {
    if (!glide.started || glide.failed || glide.score >= TARGET_SCORE) return undefined;

    let raf = 0;
    let lastFrameTime = 0;

    const step = (time) => {
      if (!lastFrameTime) {
        lastFrameTime = time;
      }

      const current = glideRef.current;
      if (!current.started || current.failed || current.score >= TARGET_SCORE) return;

      const frameDelta = Math.min(time - lastFrameTime, MAX_FRAME_DELTA) / 1000;
      lastFrameTime = time;

      const nextVelocity = current.velocity + GRAVITY * frameDelta;
      const nextY = current.ufoY + nextVelocity * frameDelta;
      let gainedScore = 0;
      let recycleX = Math.max(...current.pipes.map((pipe) => pipe.x));

      const nextPipes = current.pipes.map((pipe) => {
        let nextX = pipe.x - PIPE_SPEED * frameDelta;
        let nextPassed = pipe.passed;
        let nextGapTop = pipe.gapTop;

        if (!nextPassed && nextX + PIPE_WIDTH < UFO_X) {
          nextPassed = true;
          gainedScore += 1;
        }

        if (nextX < -PIPE_WIDTH) {
          recycleX += 220;
          nextX = recycleX;
          nextPassed = false;
          nextGapTop = 42 + Math.floor(Math.random() * 130);
        }

        return { ...pipe, x: nextX, gapTop: nextGapTop, passed: nextPassed };
      });

      const nextScore = Math.min(TARGET_SCORE, current.score + gainedScore);
      const hitBounds = nextY < 0 || nextY + UFO_SIZE > GAME_HEIGHT;
      const hitPipe = nextPipes.some((pipe) => {
        const overlapsX = UFO_X + UFO_SIZE > pipe.x && UFO_X < pipe.x + PIPE_WIDTH;
        const inGap = nextY > pipe.gapTop && nextY + UFO_SIZE < pipe.gapTop + GAP_SIZE;
        return overlapsX && !inGap;
      });

      const nextGlide = {
        ...current,
        ufoY: nextY,
        velocity: nextVelocity,
        pipes: nextPipes,
        score: nextScore,
        failed: hitBounds || hitPipe,
      };

      publishGlide(nextGlide);

      if (!nextGlide.failed && nextGlide.score < TARGET_SCORE) {
        raf = requestAnimationFrame(step);
      }
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [glide.failed, glide.score, glide.started, publishGlide]);

  const { ufoY, velocity, pipes, score, failed, started } = glide;
  const handleGlideTap = (event) => {
    event.preventDefault();
    if (failed) {
      resetGlide();
      return;
    }
    flap();
  };

  const handleGlideModalTap = (event) => {
    if (event.pointerType === "mouse") return;
    if (event.target.closest("button")) return;
    handleGlideTap(event);
  };

  return (
    <Overlay
      title="Turbo Glide"
      subtitle="Thread the ship through 10 neon gates."
      onClose={onClose}
      modalProps={{
        className: "turbo-glide-modal",
        onPointerDown: handleGlideModalTap,
      }}
      bodyProps={{
        className: "turbo-glide-touch-body",
      }}
    >
      <div
        className="mini-task-topbar"
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "linear-gradient(180deg, rgba(250,255,0,0.18), rgba(255,138,0,0.08)), rgba(7,17,31,0.9)",
            border: "2px solid rgba(250,255,0,0.34)",
            color: "#faff00",
            fontWeight: 800,
            textTransform: "uppercase",
            boxShadow: "0 0 18px rgba(250,255,0,0.18), inset 0 0 12px rgba(250,255,0,0.08)",
          }}
        >
          Cleared: {score}/{TARGET_SCORE}
        </div>
        <div style={{ color: "#b8fff2", fontWeight: 800, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.6 }}>
          Press Space, Arrow Up, or tap the screen to fly.
        </div>
      </div>

      <ScaledStage
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        className="mini-stage mini-stage-glide"
        frameClassName="task-stage-frame task-stage-frame-medium task-tap-stage-frame"
        maxScale={1.18}
        role="button"
        aria-label={failed ? "Restart Turbo Glide" : "Fly up in Turbo Glide"}
        tabIndex={0}
        onPointerDown={(event) => {
          event.stopPropagation();
          handleGlideTap(event);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleGlideTap(event);
          }
        }}
        style={{
          position: "relative",
          height: GAME_HEIGHT,
          borderRadius: 28,
          overflow: "hidden",
          background:
            "radial-gradient(circle at 18% 20%, rgba(0,217,255,0.24), transparent 18%), radial-gradient(circle at 82% 18%, rgba(255,45,85,0.2), transparent 22%), radial-gradient(circle at 54% 92%, rgba(250,255,0,0.12), transparent 28%), linear-gradient(180deg, #070b18 0%, #101142 54%, #06111d 100%)",
          border: "4px solid rgba(250,255,0,0.28)",
          boxShadow: "inset 0 0 0 4px rgba(255,255,255,0.06), inset 0 0 42px rgba(0,217,255,0.16), 0 18px 34px rgba(0,0,0,0.32)",
        }}
      >
        {[34, 92, 186, 278, 418, 500].map((x, i) => (
          <div
            key={x}
            style={{
              position: "absolute",
              left: x,
              top: [30, 64, 26, 88, 42, 72][i],
              width: i % 2 ? 3 : 5,
              height: i % 2 ? 3 : 5,
              borderRadius: "50%",
              background: i === 5 ? "#faff00" : i === 1 || i === 4 ? "#ff4fd8" : "#b8fff2",
              boxShadow: `0 0 14px ${i === 5 ? "rgba(250,255,0,0.9)" : "rgba(0,217,255,0.9)"}`,
            }}
          />
        ))}
        <div style={{ position: "absolute", right: 34, top: 22, width: 96, height: 96, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, rgba(250,255,0,0.88), rgba(255,138,0,0.52) 44%, transparent 58%)", opacity: 0.34, boxShadow: "0 0 36px rgba(250,255,0,0.2)" }} />
        <div style={{ position: "absolute", left: -40, right: -40, bottom: -26, height: 118, transform: "skewY(-5deg)", background: "linear-gradient(180deg, transparent, rgba(0,217,255,0.14) 38%, rgba(255,45,85,0.1))" }} />
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 78, background: "repeating-linear-gradient(90deg, rgba(0,217,255,0.22) 0 2px, transparent 2px 38px), repeating-linear-gradient(0deg, rgba(250,255,0,0.12) 0 2px, transparent 2px 18px)", transform: "perspective(180px) rotateX(54deg)", transformOrigin: "bottom center", opacity: 0.82 }} />
        <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 16, background: "repeating-linear-gradient(90deg, #faff00 0 22px, #ff8a00 22px 42px, #ff2d55 42px 62px, #00d9ff 62px 82px)", opacity: 0.82 }} />
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 16, background: "repeating-linear-gradient(90deg, rgba(250,255,0,0.75) 0 14px, transparent 14px 28px)", boxShadow: "0 -4px 18px rgba(250,255,0,0.2)" }} />
        {[118, 244, 372, 492].map((x) => (
          <div key={x} style={{ position: "absolute", left: x, top: 0, width: 2, height: "100%", background: "linear-gradient(180deg, transparent, rgba(0,217,255,0.2), transparent)", boxShadow: "0 0 12px rgba(0,217,255,0.22)" }} />
        ))}

        {pipes.map((pipe) => (
          <React.Fragment key={pipe.id}>
            <div
              className="glide-pipe"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: PIPE_WIDTH,
                height: pipe.gapTop,
                borderRadius: "0 0 18px 18px",
                background: "linear-gradient(180deg, rgba(0,217,255,0.26), rgba(255,45,85,0.18)), repeating-linear-gradient(0deg, rgba(255,255,255,0.18) 0 3px, transparent 3px 16px), #101142",
                border: "3px solid rgba(0,217,255,0.88)",
                boxShadow: "0 0 18px rgba(0,217,255,0.38), inset 0 0 18px rgba(255,45,85,0.18)",
                transform: `translate3d(${pipe.x}px, 0, 0)`,
                willChange: "transform",
              }}
            />
            <div
              className="glide-pipe"
              style={{
                position: "absolute",
                left: 0,
                top: pipe.gapTop - 16,
                width: PIPE_WIDTH + 12,
                height: 18,
                borderRadius: 4,
                background: "linear-gradient(90deg, #faff00, #ff8a00, #ff2d55)",
                border: "2px solid rgba(255,255,255,0.72)",
                boxShadow: "0 0 18px rgba(250,255,0,0.46)",
                transform: `translate3d(${pipe.x - 6}px, 0, 0)`,
                willChange: "transform",
              }}
            />
            <div
              className="glide-pipe"
              style={{
                position: "absolute",
                left: 0,
                top: pipe.gapTop + GAP_SIZE,
                width: PIPE_WIDTH,
                height: GAME_HEIGHT - pipe.gapTop - GAP_SIZE,
                borderRadius: "18px 18px 0 0",
                background: "linear-gradient(180deg, rgba(255,45,85,0.2), rgba(0,217,255,0.24)), repeating-linear-gradient(0deg, rgba(255,255,255,0.18) 0 3px, transparent 3px 16px), #101142",
                border: "3px solid rgba(0,217,255,0.88)",
                boxShadow: "0 0 18px rgba(0,217,255,0.38), inset 0 0 18px rgba(255,45,85,0.18)",
                transform: `translate3d(${pipe.x}px, 0, 0)`,
                willChange: "transform",
              }}
            />
            <div
              className="glide-pipe"
              style={{
                position: "absolute",
                left: 0,
                top: pipe.gapTop + GAP_SIZE,
                width: PIPE_WIDTH + 12,
                height: 18,
                borderRadius: 4,
                background: "linear-gradient(90deg, #00d9ff, #ff4fd8, #faff00)",
                border: "2px solid rgba(255,255,255,0.72)",
                boxShadow: "0 0 18px rgba(0,217,255,0.46)",
                transform: `translate3d(${pipe.x - 6}px, 0, 0)`,
                willChange: "transform",
              }}
            />
          </React.Fragment>
        ))}

        <div
          className="glide-ufo"
          style={{
            position: "absolute",
            left: UFO_X,
            top: 0,
            width: UFO_SIZE,
            height: UFO_SIZE,
            transform: `translate3d(0, ${ufoY}px, 0) rotate(${Math.max(-22, Math.min(22, velocity * 0.08))}deg)`,
            transformOrigin: "center center",
            willChange: "transform",
            filter: "drop-shadow(0 10px 14px rgba(0,0,0,0.32)) drop-shadow(0 0 14px rgba(0,217,255,0.42))",
          }}
        >
          <div style={{ position: "absolute", left: 2, top: 13, width: 30, height: 12, borderRadius: 4, background: "linear-gradient(180deg, #faff00, #ff8a00 54%, #ff2d55)", border: "2px solid #24021a", boxShadow: "inset 0 0 8px rgba(255,255,255,0.34)" }} />
          <div style={{ position: "absolute", left: 8, top: 5, width: 18, height: 15, borderRadius: "14px 14px 6px 6px", background: "radial-gradient(circle at 50% 38%, #ffffff, #00d9ff 44%, #12617b 70%)", border: "2px solid #dffbff" }} />
          <div style={{ position: "absolute", left: -2, top: 17, width: 9, height: 8, clipPath: "polygon(100% 50%, 0 0, 0 100%)", background: "#00d9ff", boxShadow: "0 0 10px rgba(0,217,255,0.8)" }} />
          <div style={{ position: "absolute", left: 27, top: 17, width: 9, height: 8, clipPath: "polygon(0 50%, 100% 0, 100% 100%)", background: "#ff4fd8", boxShadow: "0 0 10px rgba(255,79,216,0.8)" }} />
          <div style={{ position: "absolute", left: 9, top: 25, width: 16, height: 10, clipPath: "polygon(50% 100%, 0 0, 100% 0)", background: "linear-gradient(180deg, rgba(250,255,0,0.95), rgba(0,217,255,0))", filter: "blur(0.2px)" }} />
        </div>

        {failed && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(2px)",
            }}
          >
            <div
              style={{
                padding: "18px 22px",
                borderRadius: 8,
                background: "linear-gradient(180deg, rgba(40,7,25,0.96), rgba(7,17,31,0.96))",
                border: "2px solid rgba(255,45,85,0.85)",
                textAlign: "center",
                color: "#f8fafc",
                boxShadow: "0 0 26px rgba(255,45,85,0.38)",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 900, color: "#ff8a00", textTransform: "uppercase" }}>Crash!</div>
              <div style={{ marginTop: 6, color: "#b8fff2", fontWeight: 800, textTransform: "uppercase", fontSize: 12 }}>Press Space or restart.</div>
            </div>
          </div>
        )}
        {!failed && !started && score < TARGET_SCORE && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(5, 2, 13, 0.28)",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                padding: "14px 18px",
                borderRadius: 8,
                background: "linear-gradient(180deg, rgba(250,255,0,0.94), rgba(255,138,0,0.92))",
                border: "2px solid rgba(255,255,255,0.82)",
                color: "#170217",
                fontWeight: 900,
                textTransform: "uppercase",
                boxShadow: "0 0 22px rgba(250,255,0,0.42), 0 8px 0 #6d1238",
              }}
            >
              Tap or press to launch
            </div>
          </div>
        )}
      </ScaledStage>

      <div className="task-action-row task-action-row-mobile-secondary" style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>
        <GameButton onClick={flap} disabled={failed || score >= TARGET_SCORE}>Fly Up</GameButton>
        <GameButton onClick={resetGlide}>Restart</GameButton>
      </div>
    </Overlay>
  );
}

function RewardOverlay({ reward, onPick, chosen, selectedCapsule }) {
  const revealedCapsule = selectedCapsule || PRIZE_CAPSULES[0];

  return (
    <div className="arcade-overlay" style={overlayStyle}>
      <div
        className="reward-modal arcade-modal"
        style={{
          ...modalStyle,
          maxWidth: 860,
          background:
            "linear-gradient(180deg, rgba(12,19,34,0.98), rgba(7,12,25,0.96)), radial-gradient(circle at top, rgba(56,189,248,0.12), transparent 28%)",
          border: "1px solid rgba(125,211,252,0.24)",
          boxShadow: "0 28px 60px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        <div style={{ fontSize: 13, color: "#42ff87", fontWeight: 900, marginBottom: 8, letterSpacing: 1.4 }}>
          BONUS STAGE COMPLETE
        </div>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>All arcade missions cleared!</h2>

        {!chosen ? (
          <>
            <p style={{ color: "#cbd5e1", fontSize: 17, marginBottom: 0 }}>
              Choose one prize capsule from the bonus vault.
            </p>
            <div
              className="prize-capsule-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 18,
                marginTop: 22,
              }}
            >
              {PRIZE_CAPSULES.map((capsule) => (
                <button
                  key={capsule.id}
                  className="prize-capsule-card"
                  onClick={() => {
                    arcadeSound.reward();
                    onPick(capsule);
                  }}
                  style={{
                    "--capsule-glow": capsule.glow,
                    "--capsule-accent": capsule.accent,
                    border: "2px solid rgba(0,217,255,0.22)",
                    borderRadius: 8,
                    background:
                      "linear-gradient(180deg, rgba(250,255,0,0.08), transparent 22%), linear-gradient(180deg, rgba(20,13,44,0.98), rgba(5,8,22,0.98))",
                    padding: "16px 16px 14px",
                    cursor: "pointer",
                    color: "white",
                    boxShadow:
                      "0 16px 28px rgba(0,0,0,0.34), inset 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 24px rgba(0,217,255,0.08)",
                  }}
                >
                  <div
                    className="prize-capsule-art-frame"
                    style={{
                      margin: "0 auto",
                      "--capsule-position": capsule.position,
                    }}
                  >
                    <div
                      className="prize-capsule-art"
                      style={{
                        backgroundImage: `url(${arcadePrizeCapsules})`,
                        backgroundPosition: capsule.position,
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 12, fontWeight: 900, letterSpacing: 0.6 }}>Prize Capsule {capsule.id}</div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "#93c5fd" }}>Tap to reveal</div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div
            style={{
              marginTop: 22,
              "--capsule-glow": revealedCapsule.glow,
              "--capsule-accent": revealedCapsule.accent,
              padding: 28,
              borderRadius: 16,
              background:
                "linear-gradient(180deg, rgba(250,255,0,0.08), transparent 18%), radial-gradient(circle at 50% 18%, color-mix(in srgb, var(--capsule-glow) 18%, transparent), transparent 38%), linear-gradient(180deg, rgba(13,18,38,0.96), rgba(5,7,19,0.96))",
              border: "2px solid color-mix(in srgb, var(--capsule-accent) 34%, rgba(125,211,252,0.2))",
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 38px color-mix(in srgb, var(--capsule-glow) 12%, transparent), 0 0 28px color-mix(in srgb, var(--capsule-glow) 16%, transparent)",
              textAlign: "center",
            }}
          >
            <div
              className="prize-reveal-capsule-wrap"
              style={{
                margin: "0 auto",
                "--capsule-position": revealedCapsule.position,
              }}
            >
              <div
                className="prize-reveal-capsule"
                style={{
                  backgroundImage: `url(${arcadePrizeCapsules})`,
                  backgroundPosition: revealedCapsule.position,
                }}
              />
            </div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.6, color: revealedCapsule.accent, marginTop: 16, textTransform: "uppercase" }}>
              Capsule {revealedCapsule.id} opened
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 8, textTransform: "uppercase", textShadow: "2px 2px 0 #170217, 0 0 16px color-mix(in srgb, var(--capsule-glow) 44%, transparent)" }}>{reward.label}</div>
            <div style={{ fontSize: 18, margin: "10px auto 0", maxWidth: 520, color: "#dbeafe", lineHeight: 1.45 }}>{reward.text}</div>
            <GameButton
              onClick={() => {
                arcadeSound.reward();
                window.location.assign(REWARD_CLAIM_URL);
              }}
              style={{ marginTop: 22, fontSize: 16 }}
            >
              Claim Your Rewards
            </GameButton>
          </div>
        )}
      </div>
    </div>
  );
}

function Overlay({ title, subtitle, children, onClose, bodyProps = {}, modalProps = {} }) {
  const { className: bodyClassName = "", style: bodyStyle, ...restBodyProps } = bodyProps;
  const { className: modalClassName = "", style: modalExtraStyle, ...restModalProps } = modalProps;

  return (
    <div className="arcade-overlay" style={overlayStyle}>
      <div className={`arcade-modal ${modalClassName}`.trim()} style={{ ...modalStyle, ...modalExtraStyle }} {...restModalProps}>
        <div className="arcade-modal-header" style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center" }}>
          <div style={{ textAlign: "left" }}>
            <h2 style={{ margin: "0 0 6px" }}>{title}</h2>
            <div className="arcade-modal-subtitle" style={{ color: "#cbd5e1" }}>{subtitle}</div>
          </div>
          <GameButton
            onClick={() => {
              arcadeSound.taskClose();
              onClose();
            }}
            style={{ padding: "10px 14px" }}
          >
            Close
          </GameButton>
        </div>
        <div className={`arcade-modal-body ${bodyClassName}`.trim()} style={{ marginTop: 18, ...bodyStyle }} {...restBodyProps}>{children}</div>
      </div>
    </div>
  );
}

function getDeviceMode() {
  if (typeof window === "undefined") {
    return { isTouch: false, isPortrait: false };
  }

  const coarsePointer = window.matchMedia ? window.matchMedia("(pointer: coarse)").matches : false;
  const noHover = window.matchMedia ? window.matchMedia("(hover: none)").matches : false;
  const hasTouchPoints = navigator.maxTouchPoints > 0;
  const isTouch = coarsePointer || noHover || hasTouchPoints || "ontouchstart" in window;
  const isPortrait = window.innerHeight >= window.innerWidth;

  return { isTouch, isPortrait };
}

function syncVisualViewportHeight() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
  if (Number.isFinite(height) && height > 0) {
    document.documentElement.style.setProperty("--app-visual-height", `${height}px`);
  }
}

export default function App() {
  const [playerName] = useState(() => funnyNames[Math.floor(Math.random() * funnyNames.length)]);
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [activeTask, setActiveTask] = useState(null);
  const [nearTask, setNearTask] = useState(() => getNearTaskForPlayer(INITIAL_PLAYER, INITIAL_TASKS));
  const [currentRoom, setCurrentRoom] = useState(() => getRoomNameForPlayer(INITIAL_PLAYER));
  const [rewardChosen, setRewardChosen] = useState(false);
  const [selectedRewardCapsule, setSelectedRewardCapsule] = useState(null);
  const [reward, setReward] = useState(null);
  const [successBadge, setSuccessBadge] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [showIntroFlyover, setShowIntroFlyover] = useState(false);
  const [introLeaving, setIntroLeaving] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [deviceMode, setDeviceMode] = useState(getDeviceMode);
  const pressedRef = useRef({});
  const playerRef = useRef(INITIAL_PLAYER);
  const playerNodeRef = useRef(null);
  const facingRef = useRef("right");
  const nearTaskRef = useRef(getNearTaskForPlayer(INITIAL_PLAYER, INITIAL_TASKS));
  const tasksRef = useRef(INITIAL_TASKS);
  const currentRoomRef = useRef(getRoomNameForPlayer(INITIAL_PLAYER));
  const totalTasks = taskPoints.length;

  const completedCount = Object.values(tasks).filter(Boolean).length;
  const allDone = completedCount === totalTasks;
  const progressPercent = `${(completedCount / totalTasks) * 100}%`;
  const gameLocked = showIntro || showWelcome || Boolean(activeTask) || allDone;
  const shellClassName = [
    "app-shell",
    deviceMode.isTouch ? "is-touch-device" : "is-pointer-device",
    deviceMode.isPortrait ? "is-portrait" : "is-landscape",
  ].join(" ");

  useEffect(() => {
    const updateDeviceMode = () => {
      syncVisualViewportHeight();
      setDeviceMode(getDeviceMode());
    };
    updateDeviceMode();

    const mediaQueries = [];
    if (window.matchMedia) {
      mediaQueries.push(window.matchMedia("(pointer: coarse)"));
      mediaQueries.push(window.matchMedia("(hover: none)"));
      mediaQueries.push(window.matchMedia("(orientation: portrait)"));
    }

    mediaQueries.forEach((query) => {
      if (query.addEventListener) {
        query.addEventListener("change", updateDeviceMode);
      } else if (query.addListener) {
        query.addListener(updateDeviceMode);
      }
    });

    window.addEventListener("resize", updateDeviceMode);
    window.addEventListener("orientationchange", updateDeviceMode);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateDeviceMode);
      window.visualViewport.addEventListener("scroll", updateDeviceMode);
    }

    return () => {
      mediaQueries.forEach((query) => {
        if (query.removeEventListener) {
          query.removeEventListener("change", updateDeviceMode);
        } else if (query.removeListener) {
          query.removeListener(updateDeviceMode);
        }
      });
      window.removeEventListener("resize", updateDeviceMode);
      window.removeEventListener("orientationchange", updateDeviceMode);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateDeviceMode);
        window.visualViewport.removeEventListener("scroll", updateDeviceMode);
      }
    };
  }, []);

  const applyPlayerTransform = (nextPlayer, nextFacing = facingRef.current) => {
    if (!playerNodeRef.current) return;
    playerNodeRef.current.style.transform = `translate3d(${nextPlayer.x}px, ${nextPlayer.y}px, 0) scaleX(${nextFacing === "left" ? -1 : 1})`;
  };

  const syncPlayerContext = (nextPlayer, taskState = tasksRef.current) => {
    const nextRoom = getRoomNameForPlayer(nextPlayer);
    if (nextRoom !== currentRoomRef.current) {
      currentRoomRef.current = nextRoom;
      setCurrentRoom(nextRoom);
      arcadeSound.roomChange();
    }

    const nextNearTask = getNearTaskForPlayer(nextPlayer, taskState);
    const previousNearId = nearTaskRef.current ? nearTaskRef.current.id : null;
    const nextNearId = nextNearTask ? nextNearTask.id : null;
    if (previousNearId !== nextNearId) {
      nearTaskRef.current = nextNearTask;
      setNearTask(nextNearTask);
      if (nextNearTask) {
        arcadeSound.nearTask();
      }
    }
  };

  const startNearTask = useCallback(() => {
    if (gameLocked) return;
    const taskToStart = nearTaskRef.current;
    if (taskToStart) {
      arcadeSound.taskOpen();
      pressedRef.current = {};
      setActiveTask(taskToStart.id);
    }
  }, [gameLocked]);

  const setMobileMoveKey = useCallback((key, isPressed) => {
    arcadeSound.unlock();
    if (gameLocked && isPressed) return;
    pressedRef.current[key] = isPressed;
  }, [gameLocked]);

  useEffect(() => {
    tasksRef.current = tasks;
    syncPlayerContext(playerRef.current, tasks);
  }, [tasks]);

  useEffect(() => {
    if (gameLocked) {
      pressedRef.current = {};
      arcadeSound.move(false);
    }
  }, [gameLocked]);

  useEffect(() => {
    const flyoverTimer = setTimeout(() => setShowIntroFlyover(true), 1000);
    const flyoverSoundTimer = setTimeout(() => arcadeSound.introFlyby(), 1000);
    const fadeTimer = setTimeout(() => setIntroLeaving(true), 3200);
    const hideTimer = setTimeout(() => setShowIntro(false), 3900);
    return () => {
      clearTimeout(flyoverTimer);
      clearTimeout(flyoverSoundTimer);
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    const keyDown = (e) => {
      arcadeSound.unlock();
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)) {
        e.preventDefault();
      }
      if (gameLocked) return;
      pressedRef.current[e.key.toLowerCase()] = true;

      if (e.key === "e" || e.key === "E") {
        startNearTask();
      }
    };

    const keyUp = (e) => {
      pressedRef.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    };
  }, [gameLocked, startNearTask]);

  useEffect(() => {
    if (gameLocked) return undefined;

    let raf = 0;
    let lastFrameTime = 0;

    const tick = (time) => {
      if (!lastFrameTime) {
        lastFrameTime = time;
      }

      const frameDelta = Math.min(time - lastFrameTime, MAX_FRAME_DELTA);
      const step = SPEED * (frameDelta / 1000);
      lastFrameTime = time;

      const p = pressedRef.current;
      const currentPlayer = playerRef.current;
      let dx = 0;
      let dy = 0;

      if (p["arrowup"] || p["w"]) dy -= step;
      if (p["arrowdown"] || p["s"]) dy += step;
      if (p["arrowleft"] || p["a"]) dx -= step;
      if (p["arrowright"] || p["d"]) dx += step;

      if (dx !== 0 || dy !== 0) {
        if (dx !== 0 && dy !== 0) {
          const diagonal = Math.SQRT1_2;
          dx *= diagonal;
          dy *= diagonal;
        }

        const attemptMove = (from, moveX, moveY) => ({
          x: clamp(from.x + moveX, 12, GAME_W - PLAYER_W - 12),
          y: clamp(from.y + moveY, 12, GAME_H - PLAYER_H - 12),
        });

        let nextPlayer = attemptMove(currentPlayer, dx, dy);
        if (!canPlayerMoveTo(nextPlayer)) {
          nextPlayer = currentPlayer;
          const xOnly = attemptMove(currentPlayer, dx, 0);
          if (dx !== 0 && canPlayerMoveTo(xOnly)) {
            nextPlayer = xOnly;
          }
          const yOnly = attemptMove(nextPlayer, 0, dy);
          if (dy !== 0 && canPlayerMoveTo(yOnly)) {
            nextPlayer = yOnly;
          }
        }

        if (nextPlayer.x !== currentPlayer.x || nextPlayer.y !== currentPlayer.y) {
          const nextFacing = dx < 0 ? "left" : dx > 0 ? "right" : facingRef.current;
          facingRef.current = nextFacing;
          playerRef.current = nextPlayer;
          applyPlayerTransform(nextPlayer, nextFacing);
          syncPlayerContext(nextPlayer);
          arcadeSound.move(true);
        } else {
          arcadeSound.move(false);
        }
      } else {
        arcadeSound.move(false);
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gameLocked]);

  useEffect(() => {
    applyPlayerTransform(playerRef.current, facingRef.current);
  }, []);

  const completeTask = (id) => {
    arcadeSound.clear();
    setTasks((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    setActiveTask(null);
    setSuccessBadge({
      title: "Stage Clear",
      subtitle: `${STAGE_META[id].label} completed`,
    });
  };

  useEffect(() => {
    if (!successBadge) return undefined;
    const timer = setTimeout(() => setSuccessBadge(null), 1400);
    return () => clearTimeout(timer);
  }, [successBadge]);

  return (
    <div className={shellClassName}>
      <div className="app-bg-art" aria-hidden="true" />
      <div className="arcade-background-runners" aria-hidden="true">
        <span className="arcade-runner arcade-runner-a" />
        <span className="arcade-runner arcade-runner-b" />
        <span className="arcade-runner arcade-runner-c" />
        <span className="arcade-runner arcade-runner-d" />
        <span className="arcade-runner arcade-runner-e" />
        <span className="arcade-runner arcade-runner-f" />
      </div>
      <div className="crt-scanlines" aria-hidden="true" />
      <div className="app-backdrop-glow app-backdrop-glow-left" />
      <div className="app-backdrop-glow app-backdrop-glow-right" />
      <SpaceDecor />
        <div className="arcade-content" style={{ maxWidth: 1260, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div className="top-layout">
            <div className="top-hud-left top-hero-card">
              <div className="top-hero-main">
                <div className="top-hero-copy">
                  <div className="top-hero-copy-main">
                    <img className="top-hud-logo" src={etcLogoWhite} alt="ETC logo" />
                    <div className="top-hud-title-row">
                      <h1 className="arcade-title" style={{ margin: 0, fontSize: 42 }}>ETC Space Arcade</h1>
                      <div className="top-hud-rank">PLAY HARD. SKILL HARDER.</div>
                    </div>
                    <div className="top-hud-subtitle">
                      Clear four cabinet missions, light up the board, and unlock the bonus-stage prize.
                    </div>
                  </div>
                  <div className="top-hud-meta">
                    <div className="top-hud-meta-card top-hud-meta-card-controls">
                      <span className="top-hud-meta-label">Controls</span>
                      <div className="controls-card">
                        <div className="controls-layout">
                          <div className="controls-arrows-block">
                            <div className="controls-arrows">
                              <span className="control-key control-key-up">{"\u2191"}</span>
                              <div className="controls-arrows-row">
                                <span className="control-key">{"\u2190"}</span>
                                <span className="control-key">{"\u2193"}</span>
                                <span className="control-key">{"\u2192"}</span>
                              </div>
                            </div>
                            <div className="controls-bottom-row">
                              <span className="control-key control-key-space">Space</span>
                              <span className="control-key control-key-action">E</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="top-hud-meta-card top-hud-meta-card-accent">
                      <span className="top-hud-meta-label">Objective</span>
                      <strong>Complete every mission</strong>
                    </div>
                  </div>
                </div>
                <div className="hud-energy-card hud-energy-card-inline">
                  <div className="hud-energy-top">
                    <span>Credit Power</span>
                    <span>{completedCount}/{totalTasks} cleared</span>
                  </div>
                  <div className="hud-energy-bar">
                    <div className="hud-energy-fill" style={{ width: progressPercent }} />
                  </div>
                  <div className="hud-energy-player">HI-SCORE MODE</div>
                  <div className="profile-card-divider" />
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ color: "#94a3b8", fontSize: 12 }}>Player</div>
                      <div style={{ marginTop: 4, color: "#faff00", fontWeight: 900 }}>{playerName}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#94a3b8", fontSize: 12 }}>Next Goal</div>
                      <div style={{ marginTop: 4, color: "#e2e8f0", fontWeight: 800 }}>
                        {completedCount === totalTasks ? "Claim your prize" : "Find the next lit terminal"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="top-side">
              <div style={{ ...panelStyle, paddingTop: 12, paddingRight: 12, paddingBottom: 8, paddingLeft: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div style={{ fontWeight: 900, fontSize: 20 }}>Missions: {completedCount}/{totalTasks}</div>
                  <div className="room-badge">{currentRoom}</div>
                </div>
                <div style={{ marginTop: 5, fontSize: 13, color: "#cbd5e1", lineHeight: 1.35 }}>
                  Press <b>E</b> near a task station.
                </div>

                <div style={{ marginTop: 8, display: "grid", gap: 7 }}>
                  <TaskLine label="Spark Bay - Power Links" done={tasks.wires} color={STAGE_META.wires.color} stage={STAGE_META.wires.label} />
                  <TaskLine label="Vector Lab - Tower Drop" done={tasks.maze} color={STAGE_META.maze.color} stage={STAGE_META.maze.label} />
                  <TaskLine label="Boss Deck - Blast Raid" done={tasks.shoot} color={STAGE_META.shoot.color} stage={STAGE_META.shoot.label} />
                <TaskLine label="Turbo Dock - Turbo Glide" done={tasks.flappy} color={STAGE_META.flappy.color} stage={STAGE_META.flappy.label} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...panelStyle, padding: 14 }} className="game-panel">
          <div className="phone-shell">
            <div className="phone-speaker" />
            <div className="phone-camera" />
            <ScaledStage
              width={GAME_W}
              height={GAME_H}
              className="game-board"
              frameClassName="game-viewport"
              frameStyle={{ margin: "0 auto" }}
              style={{
                position: "relative",
                borderRadius: 26,
                overflow: "hidden",
                border: "2px solid rgba(250,255,0,0.34)",
                background:
                  `linear-gradient(180deg, rgba(7,4,14,0.16), rgba(7,4,14,0.72)), url(${arcadeMapBackdrop}) center / cover no-repeat, linear-gradient(180deg, #090617, #110a2d 62%, #07040d)`,
                boxShadow: "inset 0 0 54px rgba(0,217,255,0.08), inset 0 0 0 6px rgba(255,255,255,0.035)",
              }}
            >
              <div className="map-backdrop-art" />
              <div className="map-neon-grid" />
              <div className="game-board-frame" />
              <Starfield />

            {rooms.map((room) => (
              <div
                key={room.id}
                className={`room-card room-card-${room.theme}`}
                style={{
                  position: "absolute",
                  left: room.x,
                  top: room.y,
                  width: room.w,
                  height: room.h,
                  borderRadius: 10,
                  border: `3px solid ${room.accent}`,
                  background: `radial-gradient(circle at 50% 10%, ${room.color1}55, transparent 32%), radial-gradient(circle at 82% 72%, ${room.accent}30, transparent 36%), linear-gradient(180deg, #101726 0%, ${room.color2} 100%)`,
                  boxShadow: `inset 0 0 0 4px rgba(255,255,255,0.1), inset 0 -26px 34px rgba(0,0,0,0.46), inset 0 0 34px ${room.accent}22, 0 0 28px ${room.accent}66, 0 18px 28px rgba(0,0,0,0.44)`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 3,
                    right: 3,
                    top: 3,
                    height: 18,
                    borderRadius: "18px 18px 10px 10px",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.04))",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 8,
                    right: 8,
                    bottom: 6,
                    height: 22,
                    borderRadius: "10px 10px 16px 16px",
                    background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.18))",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 5,
                    top: 8,
                    bottom: 10,
                    width: 14,
                    borderRadius: 10,
                    background: "linear-gradient(90deg, rgba(255,255,255,0.2), rgba(255,255,255,0.03))",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: 10,
                    top: 18,
                    width: 26,
                    height: 12,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.18)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 8,
                    borderRadius: 18,
                    border: "2px solid rgba(13,20,28,0.4)",
                    background:
                      `radial-gradient(circle at 20% 28%, ${room.accent}24, transparent 30%), linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.18)), repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 24px), repeating-linear-gradient(90deg, ${room.accent}12 0 1px, transparent 1px 24px)`,
                    opacity: 0.76,
                  }}
                />
                <div
                  className="map-room-name"
                  style={{
                    position: "absolute",
                    left: 14,
                    top: 10,
                    fontSize: 12,
                    letterSpacing: 1.4,
                    color: "#e6edf5",
                    textTransform: "uppercase",
                    fontWeight: 800,
                    background: "rgba(18,24,33,0.58)",
                    padding: "5px 9px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.12)",
                    zIndex: 20,
                    pointerEvents: "none",
                  }}
                >
                  {room.name}
                </div>
                <RoomInterior room={room} />
              </div>
            ))}

              <HallwayDecor />
              {walls.map((wall, index) => (
                <WallBlock key={index} wall={wall} />
              ))}

              {taskPoints.map((task) => (
                <TaskMarker key={task.id} task={task} done={tasks[task.id]} />
              ))}

                <PlayerAvatar ref={playerNodeRef} initialX={INITIAL_PLAYER.x} initialY={INITIAL_PLAYER.y} />

              {nearTask && !activeTask && !allDone && (
                <div
                  className="action-prompt"
                  style={{
                    position: "absolute",
                    top: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "linear-gradient(180deg, rgba(250,255,0,0.96), rgba(255,138,0,0.94))",
                    color: "#170217",
                    border: "2px solid rgba(255,255,255,0.82)",
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontWeight: 900,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    zIndex: 20,
                    boxShadow: "0 0 22px rgba(250,255,0,0.45), 0 10px 0 #6d1238, 0 18px 24px rgba(0,0,0,0.32)",
                  }}
                >
                  <span className="action-prompt-desktop">Press E: {nearTask.name}</span>
                  <span className="action-prompt-touch">Tap Play: {nearTask.name}</span>
                </div>
              )}
            </ScaledStage>
            <MobileControls
              disabled={gameLocked}
              canAct={Boolean(nearTask)}
              onMoveStart={(key) => setMobileMoveKey(key, true)}
              onMoveEnd={(key) => setMobileMoveKey(key, false)}
              onAction={startNearTask}
            />
          </div>
        </div>
      </div>

      {activeTask === "wires" && <WiresTask onDone={() => completeTask("wires")} onClose={() => setActiveTask(null)} />}
      {activeTask === "maze" && <MazeTask onDone={() => completeTask("maze")} onClose={() => setActiveTask(null)} />}
      {activeTask === "shoot" && <ShootTask onDone={() => completeTask("shoot")} onClose={() => setActiveTask(null)} />}
      {activeTask === "flappy" && <FlappyTask onDone={() => completeTask("flappy")} onClose={() => setActiveTask(null)} />}

      {allDone && (
        <RewardOverlay
          reward={reward}
          chosen={rewardChosen}
          selectedCapsule={selectedRewardCapsule}
          onPick={(capsule) => {
            setSelectedRewardCapsule(capsule);
            setReward(pickReward());
            setRewardChosen(true);
          }}
        />
      )}

      <OrientationNotice />
      {successBadge && <SuccessBadge title={successBadge.title} subtitle={successBadge.subtitle} />}
      {showWelcome && <WelcomeOverlay onStart={() => setShowWelcome(false)} />}
      {showIntro && <IntroSplash leaving={introLeaving} />}
      {showIntro && showIntroFlyover && <IntroFlyoverUfo />}
    </div>
  );
}

function IntroFlyoverUfo() {
  return (
    <div className="intro-flyover-ufo">
      <div className="intro-transition-ufo-shadow" />
      <div className="intro-transition-ufo-beam" />
      <div className="intro-transition-ufo-body">
        <div className="intro-transition-ufo-rim" />
        <div className="intro-transition-ufo-dome">
          <div className="intro-transition-ufo-alien-head" />
          <div className="intro-transition-ufo-alien-eye intro-transition-ufo-alien-eye-left" />
          <div className="intro-transition-ufo-alien-eye intro-transition-ufo-alien-eye-right" />
        </div>
        <div className="intro-transition-ufo-light intro-transition-ufo-light-left" />
        <div className="intro-transition-ufo-light intro-transition-ufo-light-mid" />
        <div className="intro-transition-ufo-light intro-transition-ufo-light-right" />
      </div>
    </div>
  );
}

function IntroSplash({ leaving }) {
  return (
    <div
      className={`intro-splash ${leaving ? "intro-splash-leaving" : ""}`}
      style={{ "--intro-bg": `url(${arcadeIntroBackdrop})` }}
    >
      <div className="intro-splash-noise" />
      <div className="intro-splash-glow intro-splash-glow-left" />
      <div className="intro-splash-glow intro-splash-glow-right" />
      <div className="intro-splash-ring intro-splash-ring-a" />
      <div className="intro-splash-ring intro-splash-ring-b" />
      <div className="intro-splash-streak intro-splash-streak-a" />
      <div className="intro-splash-streak intro-splash-streak-b" />
      <div className="intro-splash-streak intro-splash-streak-c" />

      <div className="intro-splash-center">
        <div className="intro-coin-slot">
          <span />
          <span />
        </div>
        <div className="intro-splash-logo-wrap">
          <img className="intro-splash-logo" src={etcLogoWhite} alt="ETC logo" />
          <div className="intro-splash-logo-scan" />
        </div>
        <div className="intro-splash-title">ETC SPACE ARCADE</div>
        <div className="intro-splash-subtitle">Press Start / Bonus Cabinet Online</div>
      </div>
    </div>
  );
}

function WelcomeOverlay({ onStart }) {
  return (
    <div className="welcome-overlay">
      <div className="welcome-card">
        <div className="welcome-badge">Power Up</div>
        <h2 className="welcome-title">ETC SPACE ARCADE</h2>
        <p className="welcome-copy">
          Four stages are live inside the station cabinet. Clear every room to unlock the bonus prize.
        </p>

        <div className="welcome-section">
          <div className="welcome-section-title">How To Play</div>
          <div className="welcome-list">
            <div>Move through the cabinet map and enter each lit room.</div>
            <div>Press <b>E</b> or tap <b>Play</b> near a glowing terminal to start a mission.</div>
            <div>Clear all 4 stages to trigger the bonus vault.</div>
          </div>
        </div>

        <div className="welcome-section">
          <div className="welcome-section-title">Controls</div>
          <div className="welcome-controls">
            <div className="controls-arrows-block">
              <div className="controls-arrows">
                <span className="control-key control-key-up">{"\u2191"}</span>
                <div className="controls-arrows-row">
                  <span className="control-key">{"\u2190"}</span>
                  <span className="control-key">{"\u2193"}</span>
                  <span className="control-key">{"\u2192"}</span>
                </div>
              </div>
              <div className="controls-bottom-row">
                <span className="control-key control-key-space">Space</span>
                <span className="control-key control-key-action">E</span>
              </div>
            </div>
          </div>
        </div>

        <div className="welcome-desktop-note">
          Desktop: keyboard controls. Phone: rotate to landscape and use the touch D-pad plus Play button.
        </div>

        <GameButton
          onClick={() => {
            arcadeSound.startGame();
            onStart();
          }}
          style={{ minWidth: 160, alignSelf: "center", marginTop: 4, fontSize: 18 }}
        >
          Start Game
        </GameButton>
      </div>
    </div>
  );
}

function TaskLine({ label, done, color, stage }) {
  return (
    <div
      className="task-line"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        background: "rgba(255,255,255,0.04)",
        borderRadius: 14,
        padding: "10px 12px",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 14px ${color}`,
          }}
        />
        <div>
          <div style={{ fontSize: 13, color: "#93c5fd", letterSpacing: 1 }}>{stage}</div>
          <span style={{ fontSize: 14 }}>{label}</span>
        </div>
      </div>
      <span style={{ fontWeight: 900, color: done ? "#86efac" : "#fcd34d" }}>{done ? "DONE" : "READY"}</span>
    </div>
  );
}

function HallwayDecor() {
  return (
    <>
      <div style={{ position: "absolute", left: 224, top: 118, width: 46, height: 102, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 224, top: 118, width: 126, height: 34, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 234, top: 128, width: 24, height: 82, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />
      <div style={{ position: "absolute", left: 236, top: 126, width: 102, height: 16, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />

      <div style={{ position: "absolute", left: 236, top: 410, width: 46, height: 134, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 236, top: 516, width: 114, height: 34, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 246, top: 420, width: 24, height: 114, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />
      <div style={{ position: "absolute", left: 248, top: 525, width: 90, height: 16, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />

      <div style={{ position: "absolute", left: 630, top: 118, width: 176, height: 34, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 794, top: 118, width: 46, height: 102, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 642, top: 126, width: 152, height: 16, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />
      <div style={{ position: "absolute", left: 804, top: 128, width: 24, height: 82, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />

      <div style={{ position: "absolute", left: 630, top: 514, width: 176, height: 34, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 794, top: 410, width: 46, height: 138, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 642, top: 523, width: 152, height: 16, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />
      <div style={{ position: "absolute", left: 804, top: 420, width: 24, height: 118, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />

      <div style={{ position: "absolute", left: 302, top: 238, width: 48, height: 144, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 632, top: 238, width: 48, height: 144, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 404, top: 182, width: 172, height: 38, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 404, top: 410, width: 172, height: 38, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }} />
      <div style={{ position: "absolute", left: 312, top: 248, width: 28, height: 124, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />
      <div style={{ position: "absolute", left: 642, top: 248, width: 28, height: 124, borderRadius: 10, background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0.02) 10px 20px)" }} />
      <div style={{ position: "absolute", left: 416, top: 191, width: 148, height: 18, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />
      <div style={{ position: "absolute", left: 416, top: 420, width: 148, height: 18, borderRadius: 9, background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(255,255,255,0.02) 12px 24px)" }} />
    </>
  );
}

function Starfield() {
  return (
    <>
      <div style={{ position: "absolute", left: 72, top: 66, width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.9)" }} />
      <div style={{ position: "absolute", left: 182, top: 126, width: 4, height: 4, borderRadius: "50%", background: "rgba(191,219,254,0.9)" }} />
      <div style={{ position: "absolute", left: 826, top: 84, width: 5, height: 5, borderRadius: "50%", background: "rgba(255,255,255,0.85)" }} />
      <div style={{ position: "absolute", left: 910, top: 150, width: 3, height: 3, borderRadius: "50%", background: "rgba(253,224,71,0.9)" }} />
      <div style={{ position: "absolute", left: 88, top: 512, width: 5, height: 5, borderRadius: "50%", background: "rgba(125,211,252,0.85)" }} />
      <div style={{ position: "absolute", left: 880, top: 522, width: 4, height: 4, borderRadius: "50%", background: "rgba(255,255,255,0.9)" }} />
    </>
  );
}

function SpaceDecor() {
  const stars = [
    { left: "7%", top: "10%", size: 3, opacity: 0.8 },
    { left: "16%", top: "28%", size: 2, opacity: 0.65 },
    { left: "23%", top: "62%", size: 4, opacity: 0.72 },
    { left: "30%", top: "78%", size: 2, opacity: 0.82 },
    { left: "41%", top: "14%", size: 3, opacity: 0.76 },
    { left: "48%", top: "88%", size: 2, opacity: 0.7 },
    { left: "58%", top: "18%", size: 2, opacity: 0.88 },
    { left: "66%", top: "34%", size: 3, opacity: 0.8 },
    { left: "72%", top: "74%", size: 2, opacity: 0.72 },
    { left: "81%", top: "12%", size: 4, opacity: 0.68 },
    { left: "89%", top: "26%", size: 2, opacity: 0.84 },
    { left: "92%", top: "68%", size: 3, opacity: 0.7 },
  ];
  const shootingStars = [
    { className: "shooting-star shooting-star-a", style: { left: "12%", top: "20%" } },
    { className: "shooting-star shooting-star-b", style: { right: "12%", top: "32%" } },
    { className: "shooting-star shooting-star-c", style: { left: "52%", bottom: "18%" } },
    { className: "shooting-star shooting-star-d", style: { left: "24%", top: "54%" } },
    { className: "shooting-star shooting-star-e", style: { right: "26%", bottom: "24%" } },
  ];

  return (
    <div className="space-decor-layer" aria-hidden="true">
      {stars.map((star, index) => (
        <div
          key={`star-${index}`}
          className="space-decor-star"
          style={{ left: star.left, top: star.top, width: star.size, height: star.size, opacity: star.opacity }}
        />
      ))}
      {shootingStars.map((star, index) => (
        <div key={`shoot-${index}`} className={star.className} style={star.style} />
      ))}
    </div>
  );
}

function SuccessBadge({ title, subtitle }) {
  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 200,
        pointerEvents: "none",
        filter: "drop-shadow(0 0 22px rgba(250,255,0,0.28)) drop-shadow(0 18px 34px rgba(0,0,0,0.44))",
      }}
    >
      <div
        style={{
          position: "relative",
          minWidth: 300,
          maxWidth: "calc(100vw - 40px)",
          padding: "24px 28px 22px",
          borderRadius: 10,
          textAlign: "center",
          background:
            "linear-gradient(180deg, rgba(66,255,135,0.22), transparent 18%), linear-gradient(135deg, rgba(34,197,94,0.98), rgba(7,34,24,0.98) 46%, rgba(0,217,255,0.82))",
          border: "4px solid rgba(134,239,172,0.92)",
          boxShadow:
            "inset 0 0 0 3px rgba(255,255,255,0.12), inset 0 0 32px rgba(66,255,135,0.24), 0 0 28px rgba(66,255,135,0.38), 0 18px 0 rgba(12,77,45,0.82)",
          color: "white",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(180deg, rgba(255,255,255,0.16) 0 1px, transparent 1px 5px), repeating-linear-gradient(90deg, rgba(134,239,172,0.16) 0 10px, transparent 10px 22px)",
            opacity: 0.22,
            mixBlendMode: "screen",
          }}
        />
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            <div
              style={{
                position: "absolute",
                left: 12 + i * 12,
                top: 10,
                width: 7,
                height: 7,
                borderRadius: 2,
                background: i === 1 ? "#00d9ff" : "#42ff87",
                boxShadow: "0 0 10px rgba(66,255,135,0.82)",
              }}
            />
            <div
              style={{
                position: "absolute",
                right: 12 + i * 12,
                bottom: 10,
                width: 7,
                height: 7,
                borderRadius: 2,
                background: i === 1 ? "#faff00" : "#42ff87",
                boxShadow: "0 0 10px rgba(66,255,135,0.82)",
              }}
            />
          </React.Fragment>
        ))}
        <div
          style={{
            position: "relative",
            width: 58,
            height: 58,
            margin: "0 auto 10px",
            borderRadius: 8,
            background: "linear-gradient(180deg, #bbf7d0, #42ff87 48%, #16a34a)",
            border: "3px solid rgba(236,253,245,0.92)",
            boxShadow: "0 0 24px rgba(66,255,135,0.58), inset 0 0 12px rgba(255,255,255,0.28)",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 14,
              top: 15,
              width: 28,
              height: 18,
              borderLeft: "7px solid #170217",
              borderBottom: "7px solid #170217",
              transform: "rotate(-45deg)",
              borderRadius: 2,
            }}
          />
        </div>
        <div
          style={{
            position: "relative",
            fontSize: 12,
            color: "#bbf7d0",
            fontWeight: 900,
            letterSpacing: 2,
            textTransform: "uppercase",
            textShadow: "0 0 12px rgba(66,255,135,0.82)",
          }}
        >
          Stage Clear
        </div>
        <div
          style={{
            position: "relative",
            marginTop: 5,
            fontSize: 28,
            fontWeight: 900,
            textTransform: "uppercase",
            color: "#ffffff",
            textShadow: "0 0 10px rgba(66,255,135,0.78), 0 0 18px rgba(0,217,255,0.34)",
          }}
        >
          {title}
        </div>
        <div
          style={{
            position: "relative",
            margin: "8px auto 0",
            maxWidth: 260,
            fontSize: 14,
            fontWeight: 900,
            color: "#d9ff63",
            textTransform: "uppercase",
            textShadow: "0 0 10px rgba(134,239,172,0.64)",
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
}

const panelStyle = {
  background: "linear-gradient(180deg, rgba(18, 8, 34, 0.92), rgba(6, 8, 18, 0.94))",
  border: "2px solid rgba(0,217,255,0.22)",
  borderRadius: 12,
  padding: 18,
  boxShadow: "0 0 24px rgba(0,217,255,0.14), 0 18px 34px rgba(0,0,0,0.42), inset 0 0 0 1px rgba(255,255,255,0.05)",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,0,12,0.82)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "clamp(10px, 2.5vw, 20px)",
  zIndex: 100,
  overflowY: "auto",
  overscrollBehavior: "contain",
};

const modalStyle = {
  width: "100%",
  maxWidth: 760,
  maxHeight: "calc(100vh - 24px)",
  overflowY: "auto",
  boxSizing: "border-box",
  background: "linear-gradient(180deg, rgba(24, 9, 46, 0.98), rgba(7, 5, 18, 0.98))",
  border: "2px solid rgba(250,255,0,0.22)",
  borderRadius: 14,
  padding: "clamp(14px, 2.6vw, 22px)",
  color: "white",
  boxShadow: "0 0 34px rgba(255,45,85,0.16), 0 24px 60px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
};
